/* ==========================================================================
   Green Beast Gaming — Build finder  <gbg-build-finder>
   No dependencies. Powered by Steam + Cloudflare Worker proxy.

   Data
     Pinned games   server-rendered blocks (cover, FPS label, optional manual specs)
     New & trending SteamSpy top100in2weeks via Cloudflare Worker proxy
     Search         Instant local search against cached Steam app list (jsDelivr CDN)
     PC specs       Steam Store appdetails via Cloudflare Worker proxy, parsed into rows
     Game Art       Deterministic Steam CDN library images (600x900 and header banners)

   Caching
     Trending cached 12h per browser. App list cached 24h. Game details load
     only when someone opens specs or continues, cached 7 days.

   Hand-off (for the customizer)
     On "Customize my Build!" the selection is saved to
       sessionStorage["gbg:build-request"]
     and a cancelable `gbg:build-request` event (detail = same object) bubbles
     from <gbg-build-finder>. If nothing calls preventDefault(), the browser goes
     to the customizer link (section setting) or the chosen case's collection,
     with ?gbg_games=appid,appid&gbg_case=handle appended.
   ========================================================================== */
(() => {
  'use strict';

  if (!('customElements' in window) || customElements.get('gbg-build-finder')) return;

  const TIER_COPY = {
    minimum: 'Get in the game at the best value.',
    recommended: 'Play the games as the developers intended.',
    boost: 'Play at very high settings with plenty of power for other games.',
  };

  const STEAM_PROXY = 'https://gbg-api.gbgcloudhelper.workers.dev/steam';
  const STEAM_CDN = 'https://cdn.akamai.steamstatic.com/steam/apps';
  const HOUR = 3600e3;
  // Fixed search-dropdown tile size (also set in CSS on .gbgb-results-dropdown
  // .gbgb-game__art) — kept as one constant so the JS row-count math and the CSS art
  // height can never drift out of sync with each other.
  const DROPDOWN_TILE_ART_HEIGHT = 110;
  const DROPDOWN_TILE_TEXT_HEIGHT = 44;
  const TTL = { trending: 12 * HOUR, search: 24 * HOUR, game: 7 * 24 * HOUR };
  const STORE = 'gbgb:v1:';
  const REQUEST_KEY = 'gbg:build-request';

  /* ---------- Modular build description generator ---------- */
  /**
   * generateModularBlurb(cpu, gpu, games, tier)
   *
   * cpu:   "Ryzen 7 9800X3D", "Core i5-14400F", "Ultra 7 265K", ...
   * gpu:   "RTX 5070", "GTX 1660 Super", "Radeon RX 9070 XT", "Arc B580", ...
   * games: ["PUBG: BATTLEGROUNDS", "Rust"]
   * tier:  "Minimum" | "Recommended" | "Performance Boost" (case-insensitive)
   */
  function generateModularBlurb(cpu, gpu, games = [], tier = 'Recommended') {
    // "an" for names that start with a vowel sound: Ultra, Arc, RTX ("ar-tee-ex").
    // "a" for everything else: Ryzen, Core, GTX, Radeon.
    const a = (name) => `${/^(rtx|[aeiou])/i.test(name.trim()) ? 'an' : 'a'} ${name}`;

    // Every picked game is named — never summarized as "and N more".
    const gameList = games.length
      ? games.join(', ').replace(/, ([^,]*)$/, ' and $1')
      : "today's top games";

    const library = {
      'Minimum': {
        intros: [
          'Get in the game at the best value.',
          'Your entry point into PC gaming is here.',
          'Great value without sacrificing gameplay.',
        ],
        hardware: [
          `Powered by ${a(cpu)} and ${a(gpu)}, this system is tuned for smooth, stable gameplay.`,
          `We've paired the ${cpu} with the ${gpu} to deliver dependable performance on a budget.`,
        ],
        outros: [
          `It's configured to handle ${gameList} with ease.`,
          `Jump straight into ${gameList} right out of the box.`,
        ],
      },
      'Recommended': {
        intros: [
          'Play your games exactly as the developers intended.',
          'The sweet spot between price and performance.',
          'Step up to a smoother, sharper gaming experience.',
        ],
        hardware: [
          `Equipped with ${a(cpu)} and ${a(gpu)}, this build is matched to run at the settings your games were designed for.`,
          `The ${cpu} and ${gpu} are balanced so neither one holds the other back.`,
        ],
        outros: [
          `Expect smooth gameplay in ${gameList}.`,
          `Built around ${gameList}, with room for the rest of your library.`,
        ],
      },
      'Performance Boost': {
        intros: [
          'Play at very high settings with power to spare.',
          'Built for players who like to turn every slider up.',
          'Our top tier for players who want more headroom.',
        ],
        hardware: [
          `The ${cpu} and ${gpu} give this build serious headroom beyond what your games require.`,
          `With ${a(gpu)} and ${a(cpu)} under the hood, there's plenty of power left for demanding new releases.`,
        ],
        outros: [
          `Turn up the settings in ${gameList} and still have power for streaming or multitasking.`,
          `Enjoy ${gameList} at very high settings, with plenty of power for whatever you play next.`,
        ],
      },
    };

    // Accept any capitalization of the tier name; fall back to Recommended.
    const tierKey = Object.keys(library).find((k) => k.toLowerCase() === String(tier).toLowerCase());
    const tierData = library[tierKey] || library['Recommended'];

    // Same configuration always gets the same text (stable across page reloads).
    const seed = [...`${cpu}|${gpu}|${tier}|${games}`].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 0);
    const pick = (arr, salt) => arr[(seed + salt) % arr.length];

    return `${pick(tierData.intros, 0)} ${pick(tierData.hardware, 1)} ${pick(tierData.outros, 2)}`;
  }

  /* ---------- Local cache (per browser, with TTL) ---------- */
  const cache = {
    get(key) {
      try {
        const raw = localStorage.getItem(STORE + key);
        if (!raw) return null;
        const item = JSON.parse(raw);
        if (Date.now() - item.t > item.ttl) {
          localStorage.removeItem(STORE + key);
          return null;
        }
        return item.v;
      } catch (e) {
        return null;
      }
    },
    set(key, v, ttl) {
      const data = JSON.stringify({ t: Date.now(), ttl, v });
      try {
        localStorage.setItem(STORE + key, data);
      } catch (e) {
        cache.purge();
        try { localStorage.setItem(STORE + key, data); } catch (e2) { /* storage full or blocked */ }
      }
    },
    purge() {
      try {
        Object.keys(localStorage).filter((k) => k.startsWith(STORE)).forEach((k) => localStorage.removeItem(k));
      } catch (e) { /* ignore */ }
    },
  };

  /* ---------- System requirements parsing ---------- */
  const LABELS = [
    ['os', ['operating system', 'os']],
    ['cpu', ['processor', 'cpu']],
    ['ram', ['system memory', 'memory', 'ram']],
    ['vram', ['video memory', 'vram']],
    ['gpu', ['graphics card', 'graphics', 'video card', 'gpu']],
    ['directx', ['directx']],
    ['storage', ['hard disk space', 'hard drive', 'hard disk', 'disk space', 'storage']],
    ['network', ['network']],
    ['sound', ['sound card', 'sound']],
    ['vr', ['vr support']],
    ['display', ['screen resolution', 'display']],
    ['notes', ['additional notes', 'notes']],
  ];
  const NAMES = {
    os: 'OS', cpu: 'Processor', ram: 'Memory', vram: 'Video memory', gpu: 'Graphics',
    directx: 'DirectX', storage: 'Storage', network: 'Network', sound: 'Sound',
    vr: 'VR', display: 'Display', notes: 'Notes', other: 'Note',
  };
  const ALIASES = [];
  LABELS.forEach(([key, words]) => words.forEach((w) => ALIASES.push([w, key])));
  ALIASES.sort((a, b) => b[0].length - a[0].length);
  const ALIAS_MAP = new Map(ALIASES);
  const escapeRe = (w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/ /g, '\\s+');
  const LABEL_RE = new RegExp(`(${ALIASES.map((a) => escapeRe(a[0])).join('|')})\\s*\\*?\\s*:`, 'gi');

  const decoder = document.createElement('textarea');
  const toText = (s) => {
    if (!s) return '';
    const t = String(s)
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(li|p|div|ul)>/gi, '\n')
      .replace(/<[^>]+>/g, '');
    decoder.innerHTML = t;
    return decoder.value.replace(/\r/g, '');
  };
  const clean = (v) => v.replace(/\s+/g, ' ').replace(/^[\s,;:\-–]+|[\s,;]+$/g, '').trim();

  // RAWG strings look like "Minimum:\nOS: Windows 10\nProcessor: …" and are
  // sometimes run together ("8 GB RAMGraphics: …"), so split on known labels.
  function parseSpecs(input) {
    const text = toText(input).replace(/^\s*(minimum|recommended)(\s+(system\s+)?requirements)?\s*:?\s*/i, '');
    if (!text.trim()) return [];
    const hits = [];
    LABEL_RE.lastIndex = 0;
    let m;
    while ((m = LABEL_RE.exec(text))) {
      const prev = m.index > 0 ? text[m.index - 1] : '';
      const atBoundary = !prev || !/[a-z]/i.test(prev) || /[A-Z]/.test(m[1][0]);
      if (!atBoundary) continue;
      hits.push({ start: m.index, end: LABEL_RE.lastIndex, key: ALIAS_MAP.get(m[1].toLowerCase().replace(/\s+/g, ' ')) });
    }
    const rows = [];
    hits.forEach((h, i) => {
      const value = clean(text.slice(h.end, i + 1 < hits.length ? hits[i + 1].start : text.length));
      if (value) rows.push({ key: h.key, label: NAMES[h.key] || h.key, value });
    });
    // Text before the first label (often "Requires a 64-bit processor…") goes last.
    const lead = clean(text.slice(0, hits.length ? hits[0].start : text.length));
    if (lead) rows.push({ key: 'other', label: NAMES.other, value: lead });
    return rows;
  }

  // Merchant-entered specs: one "Label: value" per line, labels kept as typed.
  function parseManual(text) {
    return String(text || '')
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const i = line.indexOf(':');
        const label = i > 0 ? line.slice(0, i).trim() : '';
        const value = i > 0 ? line.slice(i + 1).trim() : line;
        const key = ALIAS_MAP.get(label.toLowerCase()) || 'other';
        return { key, label: label || NAMES.other, value };
      })
      .filter((r) => r.value);
  }

  // Flat object for the customizer: { cpu, gpu, ram, ramGB, … }
  function summarize(rows) {
    const out = {};
    (rows || []).forEach((r) => {
      if (r.key !== 'other' && !(r.key in out)) out[r.key] = r.value;
    });
    const gb = (s) => {
      const m = s && s.match(/(\d+(?:\.\d+)?)\s*GB/i);
      return m ? parseFloat(m[1]) : null;
    };
    if (out.ram) out.ramGB = gb(out.ram);
    if (out.vram) out.vramGB = gb(out.vram);
    if (out.storage) out.storageGB = gb(out.storage);
    return out;
  }

  /* ---------- Steam helpers ---------- */

  // Local search against the cached Steam app list (no network call per keystroke)
  const JUNK_RE = /dedicated server|( demo$)| soundtrack| ost$| artbook| dlc$|season pass| beta$|playtest|test server|trailer/i;
  // Steam's public app list has zero content rating metadata, so this is a name-based
  // blocklist for adult/explicit titles — a family-facing PC retailer's game search must
  // not surface these regardless of what a customer types. Deliberately broad: a missed
  // edge case here is worse than an occasional legitimate title getting excluded.
  // No \b anchors on the highest-signal terms — this genre glues "sex"/"porn"/etc. onto
  // other words with no separator in EVERY position (prefix: Sexbot, Sextris; suffix:
  // CyberSex, FurryCybersex; both already proven to slip past boundary-anchored matching
  // in testing). A plain substring match is the only thing that reliably catches all of
  // that. "adult" and "strip" stay boundary-anchored since those are real words used in
  // legitimate titles (adulthood, Strip Mall Tycoon) when not glued to other adult terms.
  // "titty" (not bare "tit") — "tit" alone would false-positive on Titan/Titanium/etc.
  // \bdating\b stays boundary-anchored (unlike sex/boob/etc) since it's a common English
  // suffix — unanchored it would also block Updating/Validating/Intimidating/Accommodating.
  // \bcum\b / \bcumming\b stay boundary-anchored on both sides — "cum" is a substring of
  // scum/document/circumstance/cucumber/accumulate, so only exact-word matches are safe.
  // \bhorny\b stays boundary-anchored — "horny" is a substring of "thorny", a real word
  // (Thorny Path, Thorny Rose etc. are plausible legitimate titles).
  // furry/furries: no real word contains either, so unanchored (catches glued titles).
  // \bgay: start-anchored so it catches "Gay ..." and glued "GayBoy" but not a name
  // like "Nagaya" that merely contains the letters.
  // pregnant/pregnancy: unanchored, no innocent word contains "pregnan".
  // lesbian, lgbt: unanchored (catches plurals and LGBTQ/LGBTQIA+); no other word contains them.
  const ADULT_RE = /hentai|xxx|nsfw|erotic|fetish|porn|sex|nude|naked|brothel|harem|fuck|boob|titty|milf|hottie|furr(?:y|ies)|\bgay|pregnan(?:t|cy)|lesbian|lgbt|\bcum\b|\bcumming\b|\bhorny\b|\badult\b|\bstrip\b|stripper|striptease|\bdating\b|18\s*\+/i;

  // Deterministic Steam CDN image URLs (no API call needed)
  // Steam's release_date.date is a human string like "Oct 23, 2018" (not ISO like RAWG's
  // old feed), so pull the first 4-digit run instead of assuming the year comes first.
  const extractYear = (released) => {
    if (!released) return '';
    const m = String(released).match(/\d{4}/);
    return m ? m[0] : '';
  };

  // Newer Steam trailers (e.g. Helldivers 2) only publish HLS/DASH manifest URLs in
  // appdetails — no flat mp4/webm fields, and no predictable "movie480.mp4" pattern
  // exists for them (that guess 404s). Steam hands us the signed hls_h264 URL directly
  // though, so no scraping is needed — just a small HLS player for non-Safari browsers.
  let hlsLibPromise = null;
  const loadHlsLib = () => {
    if (window.Hls) return Promise.resolve(window.Hls);
    if (hlsLibPromise) return hlsLibPromise;
    hlsLibPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/hls.js@1/dist/hls.min.js';
      script.onload = () => resolve(window.Hls);
      script.onerror = () => reject(new Error('hls.js failed to load'));
      document.head.appendChild(script);
    });
    return hlsLibPromise;
  };
  const attachHlsTrailer = (vid, url) => {
    if (vid.canPlayType('application/vnd.apple.mpegurl')) {
      vid.src = url;
      return;
    }
    loadHlsLib()
      .then((Hls) => {
        if (!Hls || !Hls.isSupported() || !document.body.contains(vid)) return;
        const hls = new Hls();
        hls.loadSource(url);
        hls.attachMedia(vid);
        vid._hlsInstance = hls;
      })
      .catch(() => {});
  };

  const steamImg = (appid, type) => {
    if (type === 'portrait') return `${STEAM_CDN}/${appid}/library_600x900.jpg`;
    return `${STEAM_CDN}/${appid}/header.jpg`; // 460×215 banner
  };

  const makeImg = (url, w, cls, lastResort) => {
    const img = document.createElement('img');
    img.className = cls || '';
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.src = url;
    // Our constructed library/header URLs are guesses and plenty of Steam entries
    // (especially obscure ones surfaced by live search) simply don't have that asset.
    // Chain: our portrait guess -> our header guess -> Steam's own tiny_image (guaranteed
    // to exist, since it's what powers Steam's own search results) -> hide rather than
    // show a broken-image icon.
    let stage = 0;
    img.addEventListener('error', function onErr() {
      const m = url.match(/\/steam\/apps\/(\d+)\//);
      if (stage === 0 && m) {
        stage = 1;
        const hdr = steamImg(m[1], 'header');
        if (img.src !== hdr) { img.src = hdr; return; }
      }
      if (stage <= 1 && lastResort && img.src !== lastResort) {
        stage = 2;
        img.src = lastResort;
        return;
      }
      img.removeEventListener('error', onErr);
      img.style.display = 'none';
    });
    return img;
  };

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  function mapCpuToPartId(name) {
    if (!name) return 'ryzen-5500';
    const s = String(name).toLowerCase();
    if (s.includes('12400')) return 'intel-12400f';
    if (s.includes('12700')) return 'intel-12700kf';
    if (s.includes('14900')) return 'intel-14900kf';
    if (s.includes('265k')) return 'intel-265k';
    if (s.includes('285k')) return 'intel-285k';
    if (s.includes('5800')) return 'ryzen-5800x';
    if (s.includes('5900')) return 'ryzen-5900x';
    if (s.includes('5950')) return 'ryzen-5950x';
    if (s.includes('9600')) return 'ryzen-9600x';
    if (s.includes('7800')) return 'ryzen-7800x3d';
    if (s.includes('9800')) return 'ryzen-9800x3d';
    if (s.includes('9900x3d') || s.includes('9900 x3d')) return 'ryzen-9900x3d';
    if (s.includes('9900')) return 'ryzen-9900x';
    if (s.includes('9950x3d') || s.includes('9950 x3d')) return 'ryzen-9950x3d';
    if (s.includes('9950')) return 'ryzen-9950x';
    return 'ryzen-5500';
  }

  function mapGpuToPartId(name) {
    if (!name) return 'gtx-1660-super';
    const s = String(name).toLowerCase();
    if (s.includes('9070-xt') || s.includes('9070 xt')) return 'rx-9070-xt';
    if (s.includes('9070')) return 'rx-9070';
    if (s.includes('7900-xtx') || s.includes('7900 xtx')) return 'rx-7900-xtx';
    if (s.includes('7900-xt') || s.includes('7900 xt')) return 'rx-7900-xt';
    if (s.includes('5090')) return 'rtx-5090';
    if (s.includes('4090')) return 'rtx-4090';
    if (s.includes('5080')) return 'rtx-5080';
    if (s.includes('3090')) return 'rtx-3090';
    if (s.includes('5070-ti') || s.includes('5070 ti')) return 'rtx-5070-ti';
    if (s.includes('5070')) return 'rtx-5070';
    if (s.includes('5060-ti-16') || s.includes('5060 ti 16')) return 'rtx-5060-ti-16';
    if (s.includes('5060-ti') || s.includes('5060 ti')) return 'rtx-5060-ti-8';
    if (s.includes('5060')) return 'rtx-5060';
    if (s.includes('3060')) return 'rtx-3060';
    if (s.includes('5050')) return 'rtx-5050';
    if (s.includes('3050')) return 'rtx-3050';
    if (s.includes('1660')) return 'gtx-1660-super';
    return 'gtx-1660-super';
  }

  /* =============================================================================
     GBG Recommendation Engine Tier Lists
     ============================================================================= */
  const intelCpuTierList = [
    'i3 2100', 'i5 2400', 'i5 2500K', 'i7 2600K',
    'i5 3470', 'i5 3570K', 'i7 3770', 'i7 3770K',
    'i3 4130', 'i3 4150', 'i5 4460', 'i5 4590', 'i5 4670K', 'i5 4690K', 'i7 4770', 'i7 4770K', 'i7 4790', 'i7 4790K',
    'i3 6100', 'i5 6400', 'i5 6500', 'i5 6600', 'i5 6600K', 'i7 6700', 'i7 6700K',
    'i3 7100', 'i5 7400', 'i5 7500', 'i5 7600', 'i5 7600K', 'i7 7700', 'i7 7700K',
    'i3 8100', 'i5 8400', 'i5 8600K', 'i7 8700', 'i7 8700K',
    'i3 9100', 'i5 9400', 'i5 9400F', 'i5 9600K', 'i7 9700', 'i7 9700K', 'i9 9900K',
    'i3 10100', 'i5 10400', 'i5 10400F', 'i5 10600K', 'i7 10700', 'i7 10700K', 'i9 10900K',
    'i5 11400', 'i5 11400F', 'i5 11600K', 'i7 11700', 'i7 11700K', 'i9 11900K',
    'i3 12100', 'i3 12100F', 'i5 12400', 'i5 12400F', 'i5 12600K', 'i5 12600KF', 'i7 12700', 'i7 12700K', 'i7 12700KF', 'i9 12900K', 'i9 12900KF',
    'i3 13100', 'i5 13400', 'i5 13400F', 'i5 13500', 'i5 13600K', 'i5 13600KF', 'i7 13700K', 'i7 13700KF', 'i9 13900K', 'i9 13900KF',
    'i5 14400', 'i5 14400F', 'i5 14600K', 'i5 14600KF', 'i7 14700', 'i7 14700K', 'i7 14700KF', 'i9 14900K', 'i9 14900KF', 'i9 14900KS',
    'Ultra 5 245K', 'Ultra 7 265K', 'Ultra 9 285K'
  ];

  const amdCpuTierList = [
    'FX 6300', 'FX 8350', 'FX 9590',
    'Ryzen 3 1200', 'Ryzen 3 1300X', 'Ryzen 5 1400', 'Ryzen 5 1600', 'Ryzen 5 1600X', 'Ryzen 7 1700', 'Ryzen 7 1700X', 'Ryzen 7 1800X',
    'Ryzen 3 2200G', 'Ryzen 5 2400G', 'Ryzen 5 2600', 'Ryzen 5 2600X', 'Ryzen 7 2700', 'Ryzen 7 2700X',
    'Ryzen 3 3100', 'Ryzen 3 3300X', 'Ryzen 5 3400G', 'Ryzen 5 3500', 'Ryzen 5 3600', 'Ryzen 5 3600X', 'Ryzen 5 3600XT', 'Ryzen 7 3700X', 'Ryzen 7 3800X', 'Ryzen 9 3900X', 'Ryzen 9 3950X',
    'Ryzen 3 4100', 'Ryzen 5 4500', 'Ryzen 5 4600G',
    'Ryzen 5 5500', 'Ryzen 5 5600G', 'Ryzen 5 5600', 'Ryzen 5 5600X', 'Ryzen 7 5700', 'Ryzen 7 5700X', 'Ryzen 7 5700X3D', 'Ryzen 7 5800X', 'Ryzen 7 5800X3D', 'Ryzen 9 5900X', 'Ryzen 9 5950X',
    'Ryzen 5 7500F', 'Ryzen 5 7600', 'Ryzen 5 7600X', 'Ryzen 7 7700', 'Ryzen 7 7700X', 'Ryzen 7 7800X3D', 'Ryzen 9 7900', 'Ryzen 9 7900X', 'Ryzen 9 7900X3D', 'Ryzen 9 7950X', 'Ryzen 9 7950X3D',
    'Ryzen 5 8600G', 'Ryzen 7 8700G',
    'Ryzen 5 9600X', 'Ryzen 7 9700X', 'Ryzen 7 9800X3D', 'Ryzen 9 9900X', 'Ryzen 9 9900X3D', 'Ryzen 9 9950X', 'Ryzen 9 9950X3D'
  ];

  const nvidiaTierList = [
    'GTX 460', 'GTX 460 SE', 'GTX 550 Ti', 'GTX 560', 'GTX 560 Ti', 'GTX 570', 'GTX 580',
    'GT 610', 'GT 620', 'GT 630', 'GT 640', 'GT 710', 'GT 720', 'GT 730', 'GT 740',
    'GTX 650', 'GTX 650 Ti', 'GTX 650 Ti Boost', 'GTX 750', 'GTX 750 Ti', 'GT 1030',
    'GTX 660', 'GTX 660 Ti', 'GTX 760', 'GTX 670', 'GTX 770', 'GTX 680',
    'GTX 950', 'GTX 960', 'GTX 1050', 'GTX 1050 Ti', 'GTX 1060 3GB', 'GTX 1650',
    'GTX 780', 'GTX 780 Ti', 'GTX 970', 'GTX 980', 'GTX 1060 6GB', 'GTX 1650 Super',
    'GTX 1070', 'GTX 1660', 'GTX 980 Ti', 'GTX 1660 Super', 'GTX 1660 Ti', 'GTX 1070 Ti',
    'RTX 3050', 'RTX 2060', 'GTX 1080', 'RTX 2060 Super', 'RTX 2070', 'RTX 2070 Super',
    'GTX 1080 Ti', 'RTX 2080', 'RTX 3060', 'RTX 5050', 'RTX 2080 Super', 'RTX 3060 Ti', 'RTX 2080 Ti',
    'RTX 4060', 'RTX 3070', 'RTX 3070 Ti', 'RTX 4060 Ti 8GB', 'RTX 4060 Ti 16GB',
    'RTX 3080 10GB', 'RTX 4070', 'RTX 3080 12GB', 'RTX 3080 Ti', 'RTX 3090',
    'RTX 4070 Super', 'RTX 3090 Ti', 'RTX 4070 Ti', 'RTX 4070 Ti Super',
    'RTX 4080', 'RTX 4080 Super', 'RTX 4090'
  ];

  const amdTierList = [
    'HD 7750', 'HD 7770', 'HD 7790', 'HD 7850', 'R7 260', 'R7 260X', 'HD 7870',
    'R9 270', 'R9 270X', 'R9 280', 'HD 7950', 'HD 7970', 'R9 280X', 'R9 285',
    'RX 460', 'RX 560', 'R9 380', 'R9 380X', 'RX 470', 'R9 390', 'RX 480 4GB',
    'R9 390X', 'RX 480 8GB', 'R9 Fury', 'RX 570', 'R9 Fury X', 'RX 580 4GB',
    'RX 580 8GB', 'RX 590', 'RX 6400', 'RX 6500 XT', 'RX 5500 XT 4GB', 'RX 5300 XT',
    'RX 5500 XT 8GB', 'RX Vega 56', 'RX 5600 XT', 'RX Vega 64', 'RX 5700',
    'RX 6600', 'RX 5700 XT', 'RX 6600 XT', 'RX 6650 XT', 'RX 6700', 'RX 6700 XT',
    'RX 6750 XT', 'RX 6800', 'RX 6800 XT', 'RX 6900 XT', 'RX 6950 XT',
    'RX 7600', 'RX 7600 XT', 'RX 7700 XT', 'RX 7800 XT', 'RX 7900 GRE',
    'RX 7900 XT', 'RX 7900 XTX', 'RX 9070', 'RX 9070 XT'
  ];

  const intelArcTierList = [
    'Arc A310', 'Arc A380', 'Arc A580', 'Arc A750', 'Arc A770', 'Arc B580'
  ];

  /* =============================================================================
     GBG Recommendation Engine
     Dependencies: cpu-tier-list.js, gpu-tier-list.js (must load first)

     GetRecommendedGBGBuild(inputCPU, inputGPU)
       Takes any CPU and GPU string (e.g. from a game's system requirements).
       Returns the matched tier-list entry for each — the equivalent component,
       or if the match is ambiguous (e.g. "RX 580" without VRAM specified),
       the stronger variant, never a weaker one.

     Also exports getCpuTier(string) and getGpuTier(string) for individual lookups.
     ============================================================================= */

  function _normCpu(s) {
    if (!s) return '';
    return String(s)
      .replace(/^intel\s+core\s+/i, '')
      .replace(/^intel\s+/i, '')
      .replace(/^core\s+/i, '')
      .replace(/^amd\s+ryzen\s+/i, 'Ryzen ')
      .replace(/^amd\s+/i, '')
      .replace(/^ryzen\s+(\d{4})/i, 'Ryzen 5 $1')
      .replace(/-/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function _normGpu(s) {
    if (!s) return '';
    return String(s)
      .replace(/^nvidia\s+geforce\s+/i, '')
      .replace(/^nvidia\s+/i, '')
      .replace(/^amd\s+radeon\s+/i, '')
      .replace(/^amd\s+/i, '')
      .replace(/^intel\s+/i, '')
      .replace(/-/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function _findInList(normalized, list) {
    if (!list || !list.length) return null;
    var lo = normalized.toLowerCase();

    // Pass 1: exact
    for (var i = 0; i < list.length; i++) {
      if (list[i].toLowerCase() === lo) {
        return { index: i, entry: list[i] };
      }
    }

    // Pass 2: input is a prefix of list entries — take the HIGHEST (strongest)
    var best = null;
    for (var i = 0; i < list.length; i++) {
      var el = list[i].toLowerCase();
      if (el === lo || el.startsWith(lo + ' ')) {
        if (!best || i > best.index) best = { index: i, entry: list[i] };
      }
    }
    if (best) return best;

    // Pass 3: a list entry is a prefix of the input
    for (var i = 0; i < list.length; i++) {
      var el = list[i].toLowerCase();
      if (lo === el || lo.startsWith(el + ' ')) {
        return { index: i, entry: list[i] };
      }
    }

    return null;
  }

  function getCpuTier(cpuString) {
    var n = _normCpu(cpuString);
    var hit;

    hit = _findInList(n, intelCpuTierList);
    if (hit) return { entry: hit.entry, list: 'intel', index: hit.index, total: intelCpuTierList.length };

    hit = _findInList(n, amdCpuTierList);
    if (hit) return { entry: hit.entry, list: 'amd', index: hit.index, total: amdCpuTierList.length };

    return null;
  }

  function getGpuTier(gpuString) {
    var n = _normGpu(gpuString);
    var hit;

    hit = _findInList(n, nvidiaTierList);
    if (hit) return { entry: hit.entry, list: 'nvidia', index: hit.index, total: nvidiaTierList.length };

    hit = _findInList(n, amdTierList);
    if (hit) return { entry: hit.entry, list: 'amd', index: hit.index, total: amdTierList.length };

    hit = _findInList(n, intelArcTierList);
    if (hit) return { entry: hit.entry, list: 'arc', index: hit.index, total: intelArcTierList.length };

    return null;
  }

  const CUSTOMIZER_NVIDIA_OFFERINGS = [
    { id: 'gtx-1660-super', shortName: 'GTX 1660 SUPER', name: 'NVIDIA GeForce GTX 1660 SUPER 6GB', tier: 42, vram: '6 GB GDDR6', badge: '1080p Esports Value King', summary: 'Proven 6GB GDDR6 performer. Effortlessly powers competitive esports like Overwatch 2, Fortnite, and Valorant at high FPS.' },
    { id: 'rtx-3050', shortName: 'RTX 3050 8GB', name: 'NVIDIA GeForce RTX 3050 8GB', tier: 45, vram: '8 GB GDDR6', badge: 'Entry-Level RTX', summary: '8GB of VRAM with DLSS and ray tracing support for smooth 1080p gaming in esports and popular titles.' },
    { id: 'rtx-5050', shortName: 'RTX 5050 8GB', name: 'NVIDIA GeForce RTX 5050 8GB', tier: 48, vram: '8 GB GDDR6', badge: 'Next-Gen 8GB Value', summary: 'Next-gen 8GB architecture with modern DLSS support for smooth 1080p gaming across modern titles.' },
    { id: 'rtx-3060', shortName: 'RTX 3060 12GB', name: 'NVIDIA GeForce RTX 3060 12GB', tier: 53, vram: '12 GB GDDR6', badge: '12GB VRAM Value King', summary: 'Generous 12GB VRAM buffer effortlessly handles high-res textures, mods, and demanding open-world titles without stutter.' },
    { id: 'rtx-5060', shortName: 'RTX 5060 8GB', name: 'NVIDIA GeForce RTX 5060 8GB', tier: 58, vram: '8 GB GDDR7', badge: 'AI Frame Gen Speedster', summary: 'Next-gen GDDR7 performance with cutting-edge AI Frame Generation and high efficiency.' },
    { id: 'rtx-5060-ti-8', shortName: 'RTX 5060 Ti 8GB', name: 'NVIDIA GeForce RTX 5060 Ti 8GB', tier: 60, vram: '8 GB GDDR7', badge: '1440p Performance Sweet Spot', summary: 'Blazing fast 1440p gaming speed with high FP32 compute and advanced ray tracing.' },
    { id: 'rtx-5060-ti-16', shortName: 'RTX 5060 Ti 16GB', name: 'NVIDIA GeForce RTX 5060 Ti 16GB', tier: 62, vram: '16 GB GDDR7', badge: '16GB VRAM Future-Proof', summary: 'Massive 16GB GDDR7 buffer guarantees zero VRAM bottlenecks in modern titles and heavy modpacks.' },
    { id: 'rtx-5070', shortName: 'RTX 5070 12GB', name: 'NVIDIA GeForce RTX 5070 12GB', tier: 64, vram: '12 GB GDDR7', badge: '1440p Max & Ray Tracing', summary: 'Exceptional rasterization and ray-tracing performance with GDDR7 memory. Crushes 1440p at max settings.' },
    { id: 'rtx-5070-ti', shortName: 'RTX 5070 Ti 16GB', name: 'NVIDIA GeForce RTX 5070 Ti 16GB', tier: 71, vram: '16 GB GDDR7', badge: '16GB 4K Heavyweight', summary: '16GB GDDR7 memory and 8,960 CUDA cores for flawless 4K high-FPS ray-traced gaming.' },
    { id: 'rtx-3090', shortName: 'RTX 3090 24GB', name: 'NVIDIA GeForce RTX 3090 24GB', tier: 71, vram: '24 GB GDDR6X', badge: '24GB VRAM Titan', summary: 'Massive 24GB VRAM frame buffer for 4K gaming, 3D rendering, and local AI workloads.' },
    { id: 'rtx-5080', shortName: 'RTX 5080 16GB', name: 'NVIDIA GeForce RTX 5080 16GB', tier: 73, vram: '16 GB GDDR7', badge: 'Enthusiast 4K Powerhouse', summary: 'Unrelenting 4K performance with next-gen GDDR7 memory bandwidth and 10,752 CUDA cores.' },
    { id: 'rtx-4090', shortName: 'RTX 4090 24GB', name: 'NVIDIA GeForce RTX 4090 24GB', tier: 74, vram: '24 GB GDDR6X', badge: '24GB Halo Flagship', summary: '24GB GDDR6X and 16,384 CUDA cores. Monster graphics and compute performance.' },
    { id: 'rtx-5090', shortName: 'RTX 5090 32GB', name: 'NVIDIA GeForce RTX 5090 32GB', tier: 75, vram: '32 GB GDDR7', badge: 'Undisputed #1 GPU in the World', summary: '32GB GDDR7 and 21,760 CUDA cores. The absolute pinnacle of desktop graphics and AI capability.' }
  ];

  function mapToNvidiaGpu(gpuTier) {
    if (!gpuTier) return CUSTOMIZER_NVIDIA_OFFERINGS[0].shortName;

    var targetIdx = 0;
    if (gpuTier.list === 'nvidia') {
      targetIdx = gpuTier.index;
    } else if (gpuTier.list === 'amd') {
      var amdName = (gpuTier.entry || '').toUpperCase();
      if (/HD\s*7\d|R7|R9\s*2\d|R9\s*380|RX\s*4\d|RX\s*5[5-9]0|RX\s*6400|RX\s*6500/i.test(amdName)) {
        targetIdx = 42;
      } else if (/RX\s*5500\s*XT|VEGA\s*56|RX\s*5600/i.test(amdName)) {
        targetIdx = 48;
      } else if (/VEGA\s*64|RX\s*5700|RX\s*6600(?!\s*XT)/i.test(amdName)) {
        targetIdx = 53;
      } else if (/RX\s*6600\s*XT|RX\s*6650|RX\s*7600(?!\s*XT)/i.test(amdName)) {
        targetIdx = 57;
      } else if (/RX\s*6700|RX\s*6750|RX\s*7600\s*XT/i.test(amdName)) {
        targetIdx = 60;
      } else if (/RX\s*6800(?!\s*XT)|RX\s*7700/i.test(amdName)) {
        targetIdx = 61;
      } else if (/RX\s*6800\s*XT|RX\s*7800/i.test(amdName)) {
        targetIdx = 64;
      } else if (/RX\s*6900|RX\s*6950|RX\s*7900\s*GRE|RX\s*9070(?!\s*XT)/i.test(amdName)) {
        targetIdx = 65;
      } else if (/RX\s*7900\s*XT|RX\s*7900\s*XTX|RX\s*9070\s*XT/i.test(amdName)) {
        targetIdx = 71;
      } else {
        var score = gpuTier.index / Math.max(1, amdTierList.length - 1);
        targetIdx = Math.min(nvidiaTierList.length - 1, Math.round(score * 74));
      }
    } else if (gpuTier.list === 'arc') {
      var arcName = (gpuTier.entry || '').toUpperCase();
      if (/A310|A380|A580|A750/i.test(arcName)) targetIdx = 42;
      else if (/A770/i.test(arcName)) targetIdx = 48;
      else if (/B580/i.test(arcName)) targetIdx = 53;
      else targetIdx = 42;
    }

    var match = CUSTOMIZER_NVIDIA_OFFERINGS.find(function (o) { return o.tier >= targetIdx; }) || CUSTOMIZER_NVIDIA_OFFERINGS[CUSTOMIZER_NVIDIA_OFFERINGS.length - 1];
    return match.shortName;
  }

  function mapToCustomizerCpu(cpuTier, preferIntel) {
    if (!cpuTier) return preferIntel ? 'i5 12400F' : 'Ryzen 5 5500';

    if (preferIntel || cpuTier.list === 'intel') {
      var idx = cpuTier.index || 0;
      if (idx <= 60) return 'i5 12400F';
      if (idx <= 68) return 'i7 12700KF';
      if (idx <= 88) return 'i9 14900KF';
      if (idx <= 89) return 'Ultra 7 265K';
      return 'Ultra 9 285K';
    } else {
      var idxA = cpuTier.index || 0;
      if (idxA <= 31) return 'Ryzen 5 5500';
      if (idxA <= 39) return 'Ryzen 7 5800X';
      if (idxA <= 55) return 'Ryzen 5 9600X';
      if (idxA <= 56) return 'Ryzen 7 7800X3D';
      if (idxA <= 57) return 'Ryzen 7 9800X3D';
      if (idxA <= 58) return 'Ryzen 9 9900X';
      return 'Ryzen 9 9950X3D';
    }
  }

  function GetRecommendedGBGBuild(inputCPU, inputGPU) {
    var cpuTier = inputCPU ? getCpuTier(inputCPU) : null;
    var gpuTier = inputGPU ? getGpuTier(inputGPU) : null;

    var recommendedGpu = mapToNvidiaGpu(gpuTier);
    var isIntel = cpuTier && cpuTier.list === 'intel';
    var recommendedCpu = mapToCustomizerCpu(cpuTier, isIntel);

    return {
      cpu: recommendedCpu,
      gpu: recommendedGpu,
      unrecognised: [],
    };
  }

  if (typeof window !== 'undefined') {
    window.GetRecommendedGBGBuild = GetRecommendedGBGBuild;
    window.getCpuTier = getCpuTier;
    window.getGpuTier = getGpuTier;
    window._normCpu = _normCpu;
    window._normGpu = _normGpu;
  }

  function getPeakHardwareInputs(games) {
    if (!games || !games.length) return { cpuInput: null, gpuInput: null };

    let highestCpuCandidate = null;
    let highestCpuScore = -1;

    let highestGpuCandidate = null;
    let highestGpuScore = -1;

    function evalCpu(str) {
      if (!str) return;
      const parts = String(str).split(/\s+or\s+|\s*\/\s*|\s*\|\s*|,\s*/i).map(s => s.trim()).filter(Boolean);
      parts.forEach((p) => {
        const tier = getCpuTier(p);
        if (tier) {
          const score = tier.list === 'intel'
            ? (tier.index / (intelCpuTierList.length - 1)) * 100
            : (tier.index / (amdCpuTierList.length - 1)) * 100;
          if (score > highestCpuScore) {
            highestCpuScore = score;
            highestCpuCandidate = p;
          }
        } else if (highestCpuScore < 0 && !highestCpuCandidate) {
          highestCpuCandidate = p;
        }
      });
    }

    function evalGpu(str) {
      if (!str) return;
      const parts = String(str).split(/\s+or\s+|\s*\/\s*|\s*\|\s*|,\s*/i).map(s => s.trim()).filter(Boolean);
      parts.forEach((p) => {
        const tier = getGpuTier(p);
        if (tier) {
          const score = tier.list === 'nvidia'
            ? (tier.index / (nvidiaTierList.length - 1)) * 100
            : tier.list === 'amd'
            ? (tier.index / (amdTierList.length - 1)) * 100
            : (tier.index / (intelArcTierList.length - 1)) * 100;
          if (score > highestGpuScore) {
            highestGpuScore = score;
            highestGpuCandidate = p;
          }
        } else if (highestGpuScore < 0 && !highestGpuCandidate) {
          highestGpuCandidate = p;
        }
      });
    }

    games.forEach((game) => {
      const gName = (game.name || '').trim();
      for (const [title, reqs] of Object.entries(DEFAULT_CPU_TIERS.knownGameRequirements || {})) {
        if (gName.toLowerCase() === title.toLowerCase() || gName.toLowerCase().includes(title.toLowerCase())) {
          if (reqs.recIntel) evalCpu(reqs.recIntel);
          if (reqs.recRyzen) evalCpu(reqs.recRyzen);
          if (reqs.minIntel) evalCpu(reqs.minIntel);
          if (reqs.minRyzen) evalCpu(reqs.minRyzen);
        }
      }
      for (const [title, reqs] of Object.entries(DEFAULT_GPU_TIERS.knownGameRequirements || {})) {
        if (gName.toLowerCase() === title.toLowerCase() || gName.toLowerCase().includes(title.toLowerCase())) {
          if (reqs.recNvidia) evalGpu(reqs.recNvidia);
          if (reqs.recAmd) evalGpu(reqs.recAmd);
          if (reqs.minNvidia) evalGpu(reqs.minNvidia);
          if (reqs.minAmd) evalGpu(reqs.minAmd);
        }
      }

      if (game.requirements) {
        const checkRows = (rows) => {
          if (!rows) return;
          if (Array.isArray(rows)) {
            rows.forEach((r) => {
              if (r.key === 'cpu') evalCpu(r.value);
              if (r.key === 'gpu') evalGpu(r.value);
            });
          } else if (typeof rows === 'object') {
            if (rows.cpu) evalCpu(rows.cpu);
            if (rows.gpu) evalGpu(rows.gpu);
          }
        };

        if (game.requirements.recommended && game.requirements.recommended.length) {
          checkRows(game.requirements.recommended);
        } else if (game.requirements.minimum && game.requirements.minimum.length) {
          checkRows(game.requirements.minimum);
        }
      }
    });

    return {
      cpuInput: highestCpuCandidate || 'Ryzen 5 5500',
      gpuInput: highestGpuCandidate || 'RTX 5050'
    };
  }

  /* ==========================================================================
     Fallback CPU Tiers & Offerings (ensures instant evaluation even if offline)
     ========================================================================== */
  const DEFAULT_CPU_TIERS = {
    type: 'cpu',
    brands: {
      intel: {
        name: 'Intel Core',
        tierList: ['i5 7500', 'i7 7700', 'i5 12400F', 'i7 12700KF', 'i5 14400F', 'i7 14700KF', 'i9 14900KF', 'Ultra 7 265K', 'Ultra 9 285K'],
        offerings: [
          { id: 'intel-12400f', name: 'Intel Core i5-12400F', shortName: 'i5 12400F', tier: 2, minRequiredTier: 0, cores: 6, turbo: '4.4 GHz', badge: 'Value 1080p / 1440p', summary: 'Fast 6-core performance with zero bloat. Easily powers competitive esports and 1080p/1440p gaming.' },
          { id: 'intel-12700kf', name: 'Intel Core i7-12700KF', shortName: 'i7 12700KF', tier: 3, minRequiredTier: 3, cores: 12, turbo: '5.0 GHz', badge: 'High-FPS & Creator Pick', summary: '12 cores and 5.0 GHz turbo deliver butter-smooth frame pacing in AAA open worlds and multitasking.' },
          { id: 'intel-14900kf', name: 'Intel Core i9-14900KF', shortName: 'i9 14900KF', tier: 6, minRequiredTier: 4, cores: 24, turbo: '6.0 GHz', badge: 'Max Performance Flagship', summary: '24 cores clocked up to 6.0 GHz. Delivers absolute maximum framerates with unlimited headroom.' },
          { id: 'intel-265k', name: 'Intel Core Ultra 7 265K', shortName: 'Ultra 7 265K', tier: 7, minRequiredTier: 7, cores: 20, turbo: '5.5 GHz', badge: 'Next-Gen LGA1851 Creator', summary: 'Intel Arrow Lake 20-core architecture on LGA1851 with DDR5 support.' },
          { id: 'intel-285k', name: 'Intel Core Ultra 9 285K', shortName: 'Ultra 9 285K', tier: 8, minRequiredTier: 8, cores: 24, turbo: '5.7 GHz', badge: 'LGA1851 Flagship Power', summary: '24-core Intel Arrow Lake flagship with DDR5 memory bandwidth and extreme multitasking.' }
        ]
      },
      amd: {
        name: 'AMD Ryzen',
        tierList: [
          'Ryzen 4500', 'Ryzen 5500', 'Ryzen 5800X', 'Ryzen 7600X', 'Ryzen 9600X',
          'Ryzen 7800X3D', 'Ryzen 5900X', 'Ryzen 5950X', 'Ryzen 9900X', 'Ryzen 9800X3D',
          'Ryzen 7900X3D', 'Ryzen 7950X3D', 'Ryzen 9900X3D', 'Ryzen 9950X', 'Ryzen 9950X3D'
        ],
        offerings: [
          { id: 'ryzen-5500', name: 'AMD Ryzen 5 5500', shortName: 'Ryzen 5 5500', tier: 1, minRequiredTier: 0, cores: 6, turbo: '4.2 GHz', badge: 'Esports Budget Pick', summary: 'Reliable 6-core performance at exceptional value. Ideal for esports and smooth 60+ FPS gaming.' },
          { id: 'ryzen-5800x', name: 'AMD Ryzen 7 5800X', shortName: 'Ryzen 7 5800X', tier: 2, minRequiredTier: 2, cores: 8, turbo: '4.7 GHz', badge: 'High Performance 8-Core', summary: '8 cores / 16 threads up to 4.7 GHz for smooth AAA gaming and reliable multitasking.' },
          { id: 'ryzen-9600x', name: 'AMD Ryzen 5 9600X', shortName: 'Ryzen 5 9600X', tier: 4, minRequiredTier: 3, cores: 6, turbo: '5.4 GHz', badge: 'Zen 5 AM5 Next-Gen', summary: 'Blazing fast 5.4 GHz Zen 5 architecture on AM5 with DDR5 memory support.' },
          { id: 'ryzen-7800x3d', name: 'AMD Ryzen 7 7800X3D', shortName: 'Ryzen 7 7800X3D', tier: 5, minRequiredTier: 5, cores: 8, turbo: '5.0 GHz', badge: 'Elite 3D V-Cache Gaming', summary: 'Massive 3D V-Cache delivers chart-topping framerates and rock-solid 1% low FPS in open worlds.' },
          { id: 'ryzen-9800x3d', name: 'AMD Ryzen 7 9800X3D', shortName: 'Ryzen 7 9800X3D', tier: 9, minRequiredTier: 9, cores: 8, turbo: '5.2 GHz', badge: '#1 Gaming CPU in the World', summary: 'Next-gen 3D V-Cache with higher clocks and unlocked overclocking. Unchallenged gaming supremacy.' },
          { id: 'ryzen-5900x', name: 'AMD Ryzen 9 5900X', shortName: 'Ryzen 9 5900X', tier: 6, minRequiredTier: 6, cores: 12, turbo: '4.8 GHz', badge: '12-Core Multi-Thread Beast', summary: '12 cores for heavy workloads, rendering, and high-FPS gaming simultaneously.' },
          { id: 'ryzen-9900x', name: 'AMD Ryzen 9 9900X', shortName: 'Ryzen 9 9900X', tier: 8, minRequiredTier: 8, cores: 12, turbo: '5.6 GHz', badge: '12-Core Zen 5 Muscle', summary: '12 Zen 5 cores at 5.6 GHz for creators and gamers who want zero compromises.' },
          { id: 'ryzen-9900x3d', name: 'AMD Ryzen 9 9900X3D', shortName: 'Ryzen 9 9900X3D', tier: 12, minRequiredTier: 10, cores: 12, turbo: '5.5 GHz', badge: '12-Core 3D V-Cache Flagship', summary: '12 cores with 3D V-Cache for the ultimate fusion of extreme gaming and productivity.' },
          { id: 'ryzen-9950x3d', name: 'AMD Ryzen 9 9950X3D', shortName: 'Ryzen 9 9950X3D', tier: 14, minRequiredTier: 13, cores: 16, turbo: '5.7 GHz', badge: 'Apex Gaming & Workstation Flagship', summary: '16 cores, 32 threads, 3D V-Cache, and 5.7 GHz turbo. The undisputed pinnacle of desktop computing.' }
        ]
      }
    },
    knownGameRequirements: {
      'Call of Duty: Modern Warfare III': { minIntel: 'i5 7500', recIntel: 'i7 7700', minRyzen: 'Ryzen 4500', recRyzen: 'Ryzen 4500' },
      'Cyberpunk 2077': { minIntel: 'i7 7700', recIntel: 'i7 12700KF', minRyzen: 'Ryzen 5500', recRyzen: 'Ryzen 7800X3D' },
      'Cyberpunk 2077 (Phantom Liberty)': { minIntel: 'i7 7700', recIntel: 'i7 12700KF', minRyzen: 'Ryzen 5500', recRyzen: 'Ryzen 7800X3D' },
      'Cyberpunk 2077 (Phantom Liberty) Ray Tracing Ultra': { minIntel: 'i7 7700', recIntel: 'i7 14700KF', minRyzen: 'Ryzen 5500', recRyzen: 'Ryzen 7900X3D' },
      'Diablo IV': { minIntel: 'i5 7500', recIntel: 'i5 7500', minRyzen: 'Ryzen 4500', recRyzen: 'Ryzen 4500' },
      'GTA V': { minIntel: 'i5 7500', recIntel: 'i5 7500', minRyzen: 'Ryzen 4500', recRyzen: 'Ryzen 4500' },
      'Grand Theft Auto V': { minIntel: 'i5 7500', recIntel: 'i5 7500', minRyzen: 'Ryzen 4500', recRyzen: 'Ryzen 4500' },
      'Far Cry 6': { minIntel: 'i5 7500', recIntel: 'i5 12400F', minRyzen: 'Ryzen 4500', recRyzen: 'Ryzen 5500' },
      'Fortnite': { minIntel: 'i5 7500', recIntel: 'i5 7500', minRyzen: 'Ryzen 4500', recRyzen: 'Ryzen 4500' },
      'Hogwarts Legacy': { minIntel: 'i5 7500', recIntel: 'i5 7500', minRyzen: 'Ryzen 4500', recRyzen: 'Ryzen 5500' },
      'Overwatch 2': { minIntel: 'i5 7500', recIntel: 'i5 12400F', minRyzen: 'Ryzen 4500', recRyzen: 'Ryzen 5500' },
      'Rainbow 6 Siege': { minIntel: 'i5 7500', recIntel: 'i5 7500', minRyzen: 'Ryzen 4500', recRyzen: 'Ryzen 4500' },
      'Rainbow Six Siege': { minIntel: 'i5 7500', recIntel: 'i5 7500', minRyzen: 'Ryzen 4500', recRyzen: 'Ryzen 4500' },
      'Tom Clancy\'s Rainbow Six Siege': { minIntel: 'i5 7500', recIntel: 'i5 7500', minRyzen: 'Ryzen 4500', recRyzen: 'Ryzen 4500' },
      'Rocket League': { minIntel: 'i5 7500', recIntel: 'i5 7500', minRyzen: 'Ryzen 4500', recRyzen: 'Ryzen 4500' },
      'Starfield': { minIntel: 'i5 12400F', recIntel: 'i5 12400F', minRyzen: 'Ryzen 5500', recRyzen: 'Ryzen 5500' },
      'Valorant': { minIntel: 'i5 7500', recIntel: 'i5 7500', minRyzen: 'Ryzen 4500', recRyzen: 'Ryzen 4500' }
    }
  };

  /* ==========================================================================
     Fallback GPU Tiers & Offerings (ensures instant evaluation even if offline)
     ========================================================================== */
  const DEFAULT_GPU_TIERS = {
    type: 'gpu',
    brands: {
      nvidia: {
        name: 'NVIDIA GeForce',
        tierList: [
          'GTX 460', 'GTX 460 SE', 'GTX 550 Ti', 'GTX 560', 'GTX 560 Ti', 'GTX 570', 'GTX 580',
          'GT 610', 'GT 620', 'GT 630', 'GT 640', 'GT 710', 'GT 720', 'GT 730', 'GT 740',
          'GTX 650', 'GTX 650 Ti', 'GTX 650 Ti Boost', 'GTX 750', 'GTX 750 Ti', 'GT 1030',
          'GTX 660', 'GTX 660 Ti', 'GTX 760', 'GTX 670', 'GTX 770', 'GTX 680',
          'GTX 950', 'GTX 960', 'GTX 1050', 'GTX 1050 Ti', 'GTX 1060 3GB', 'GTX 1650',
          'GTX 780', 'GTX 780 Ti', 'GTX 970', 'GTX 980', 'GTX 1060 6GB', 'GTX 1650 Super',
          'GTX 1070', 'GTX 1660', 'GTX 980 Ti', 'GTX 1660 Super', 'GTX 1660 Ti', 'GTX 1070 Ti',
          'RTX 3050', 'RTX 2060', 'GTX 1080', 'RTX 2060 Super', 'RTX 2070', 'RTX 2070 Super',
          'GTX 1080 Ti', 'RTX 2080', 'RTX 3060', 'RTX 2080 Super', 'RTX 3060 Ti', 'RTX 2080 Ti',
          'RTX 4060', 'RTX 3070', 'RTX 3070 Ti', 'RTX 4060 Ti 8GB', 'RTX 4060 Ti 16GB',
          'RTX 3080 10GB', 'RTX 4070', 'RTX 3080 12GB', 'RTX 3080 Ti', 'RTX 3090',
          'RTX 4070 Super', 'RTX 3090 Ti', 'RTX 4070 Ti', 'RTX 4070 Ti Super',
          'RTX 4080', 'RTX 4080 Super', 'RTX 4090'
        ],
        offerings: [
          { id: 'gtx-1660-super', name: 'NVIDIA GeForce GTX 1660 SUPER 6GB', shortName: 'GTX 1660 SUPER', tier: 42, vram: '6 GB GDDR6', badge: '1080p Esports Value King', summary: 'Proven 6GB GDDR6 performer. Effortlessly powers competitive esports like Overwatch 2, Fortnite, and Valorant at high FPS.' },
          { id: 'rtx-3050', name: 'NVIDIA GeForce RTX 3050 8GB', shortName: 'RTX 3050 8GB', tier: 45, vram: '8 GB GDDR6', badge: 'Entry-Level RTX', summary: '8GB of VRAM with DLSS and ray tracing support for smooth 1080p gaming in esports and popular titles.' },
          { id: 'rtx-5050', name: 'NVIDIA GeForce RTX 5050 8GB', shortName: 'RTX 5050 8GB', tier: 48, vram: '8 GB GDDR6', badge: 'Next-Gen 8GB Value', summary: 'Next-gen 8GB architecture with modern DLSS support for smooth 1080p gaming across modern titles.' },
          { id: 'rtx-3060', name: 'NVIDIA GeForce RTX 3060 12GB', shortName: 'RTX 3060 12GB', tier: 53, vram: '12 GB GDDR6', badge: '12GB VRAM Value King', summary: 'Generous 12GB VRAM buffer effortlessly handles high-res textures, mods, and demanding open-world titles without stutter.' },
          { id: 'rtx-5060', name: 'NVIDIA GeForce RTX 5060 8GB', shortName: 'RTX 5060 8GB', tier: 57, vram: '8 GB GDDR7', badge: 'AI Frame Gen Speedster', summary: 'Next-gen GDDR7 performance with cutting-edge AI Frame Generation and high efficiency.' },
          { id: 'rtx-5060-ti-8', name: 'NVIDIA GeForce RTX 5060 Ti 8GB', shortName: 'RTX 5060 Ti 8GB', tier: 60, vram: '8 GB GDDR7', badge: '1440p Performance Sweet Spot', summary: 'Blazing fast 1440p gaming speed with high FP32 compute and advanced ray tracing.' },
          { id: 'rtx-5060-ti-16', name: 'NVIDIA GeForce RTX 5060 Ti 16GB', shortName: 'RTX 5060 Ti 16GB', tier: 61, vram: '16 GB GDDR7', badge: '16GB VRAM Future-Proof', summary: 'Massive 16GB GDDR7 buffer guarantees zero VRAM bottlenecks in modern titles and heavy modpacks.' },
          { id: 'rtx-5070', name: 'NVIDIA GeForce RTX 5070 12GB', shortName: 'RTX 5070 12GB', tier: 64, vram: '12 GB GDDR7', badge: '1440p Max & Ray Tracing', summary: 'Exceptional rasterization and ray-tracing performance with GDDR7 memory. Crushes 1440p at max settings.' },
          { id: 'rtx-5070-ti', name: 'NVIDIA GeForce RTX 5070 Ti 16GB', shortName: 'RTX 5070 Ti 16GB', tier: 65, vram: '16 GB GDDR7', badge: '16GB 4K Heavyweight', summary: '16GB GDDR7 memory and 8,960 CUDA cores for flawless 4K high-FPS ray-traced gaming.' },
          { id: 'rtx-3090', name: 'NVIDIA GeForce RTX 3090 24GB', shortName: 'RTX 3090 24GB', tier: 66, vram: '24 GB GDDR6X', badge: '24GB VRAM Titan', summary: 'Massive 24GB VRAM frame buffer for 4K gaming, 3D rendering, and local AI workloads.' },
          { id: 'rtx-5080', name: 'NVIDIA GeForce RTX 5080 16GB', shortName: 'RTX 5080 16GB', tier: 71, vram: '16 GB GDDR7', badge: 'Enthusiast 4K Powerhouse', summary: 'Unrelenting 4K performance with next-gen GDDR7 memory bandwidth and 10,752 CUDA cores.' },
          { id: 'rtx-4090', name: 'NVIDIA GeForce RTX 4090 24GB', shortName: 'RTX 4090 24GB', tier: 73, vram: '24 GB GDDR6X', badge: '24GB Halo Flagship', summary: '24GB GDDR6X and 16,384 CUDA cores. Monster graphics and compute performance.' },
          { id: 'rtx-5090', name: 'NVIDIA GeForce RTX 5090 32GB', shortName: 'RTX 5090 32GB', tier: 74, vram: '32 GB GDDR7', badge: 'Undisputed #1 GPU in the World', summary: '32GB GDDR7 and 21,760 CUDA cores. The absolute pinnacle of desktop graphics and AI capability.' }
        ]
      },
      amd: {
        name: 'AMD Radeon',
        tierList: [
          'HD 7750', 'HD 7770', 'HD 7790', 'HD 7850', 'R7 260', 'R7 260X', 'HD 7870',
          'R9 270', 'R9 270X', 'R9 280', 'HD 7950', 'HD 7970', 'R9 280X', 'R9 285',
          'RX 460', 'RX 560', 'R9 380', 'R9 380X', 'RX 470', 'R9 390', 'RX 480 4GB',
          'R9 390X', 'RX 480 8GB', 'R9 Fury', 'RX 570', 'R9 Fury X', 'RX 580 4GB',
          'RX 580 8GB', 'RX 590', 'RX 6400', 'RX 6500 XT', 'RX 5500 XT 4GB', 'RX 5300 XT',
          'RX 5500 XT 8GB', 'RX Vega 56', 'RX 5600 XT', 'RX Vega 64', 'RX 5700',
          'RX 6600', 'RX 5700 XT', 'RX 6600 XT', 'RX 6650 XT', 'RX 6700', 'RX 6700 XT',
          'RX 6750 XT', 'RX 6800', 'RX 6800 XT', 'RX 6900 XT', 'RX 6950 XT',
          'RX 7600', 'RX 7600 XT', 'RX 7700 XT', 'RX 7800 XT', 'RX 7900 GRE',
          'RX 7900 XT', 'RX 7900 XTX', 'RX 9070', 'RX 9070 XT'
        ],
        offerings: [
          { id: 'rx-9070', name: 'AMD Radeon RX 9070 16GB', shortName: 'RX 9070 16GB', tier: 52, vram: '16 GB GDDR6', badge: 'Next-Gen 16GB RDNA 4', summary: 'Next-gen RDNA 4 architecture with 16GB VRAM. Crushes 1440p high-refresh gaming and competitive esports.' },
          { id: 'rx-7900-xt', name: 'AMD Radeon RX 7900 XT 20GB', shortName: 'RX 7900 XT 20GB', tier: 54, vram: '20 GB GDDR6', badge: '20GB VRAM 4K Powerhouse', summary: 'Massive 20GB GDDR6 buffer on a 320-bit bus with 80MB Infinity Cache. Exceptional high-FPS 4K gaming.' },
          { id: 'rx-9070-xt', name: 'AMD Radeon RX 9070 XT 16GB', shortName: 'RX 9070 XT 16GB', tier: 56, vram: '16 GB GDDR6', badge: 'RDNA 4 Enthusiast Heavyweight', summary: 'High-clocked RDNA 4 compute units and 16GB GDDR6 memory deliver elite 1440p and 4K framerates.' },
          { id: 'rx-7900-xtx', name: 'AMD Radeon RX 7900 XTX 24GB', shortName: 'RX 7900 XTX 24GB', tier: 57, vram: '24 GB GDDR6', badge: 'AMD Flagship 24GB Beast', summary: '24GB GDDR6 memory and 384-bit bus for uncapped enthusiast 4K ultra gaming.' }
        ]
      },
      intel: {
        name: 'Intel Arc',
        tierList: ['Arc A310', 'Arc A380', 'Arc A580', 'Arc A750', 'Arc A770', 'Arc B580'],
        offerings: [
          { id: 'arc-a750', name: 'Intel Arc A750 8GB', shortName: 'Arc A750', tier: 3, vram: '8 GB GDDR6', badge: 'XeSS & AV1 Encoder', summary: 'Excellent 1080p gaming performance with dual AV1 hardware encoding engines.' },
          { id: 'arc-b580', name: 'Intel Arc B580 12GB', shortName: 'Arc B580', tier: 5, vram: '12 GB GDDR6', badge: 'Battlemage Next-Gen', summary: 'Next-generation Battlemage architecture with XeSS 2 AI frame generation.' }
        ]
      }
    },
    knownGameRequirements: {
      'Call of Duty: Modern Warfare III': { minNvidia: 'GTX 1660 Super', recNvidia: 'RTX 3060', minAmd: 'RX 580 8GB', recAmd: 'RX 7600', minIntel: 'Arc A750', recIntel: 'Arc A770' },
      'Cyberpunk 2077': { minNvidia: 'GTX 1060 6GB', recNvidia: 'RTX 2060 Super', minAmd: 'RX 580 8GB', recAmd: 'RX 5700 XT', minIntel: 'Arc A750', recIntel: 'Arc A770' },
      'Cyberpunk 2077 (Phantom Liberty)': { minNvidia: 'GTX 1060 6GB', recNvidia: 'RTX 3060', minAmd: 'RX 580 8GB', recAmd: 'RX 7600', minIntel: 'Arc A750', recIntel: 'Arc A770' },
      'Cyberpunk 2077 (Phantom Liberty) Ray Tracing Ultra': { minNvidia: 'RTX 3080 10GB', recNvidia: 'RTX 4070', minAmd: 'RX 6800 XT', recAmd: 'RX 7900 XT', minIntel: 'Arc A770', recIntel: 'Arc B580' },
      'Diablo IV': { minNvidia: 'GTX 660', recNvidia: 'GTX 1060 6GB', minAmd: 'R9 280', recAmd: 'RX 580 8GB', minIntel: 'Arc A380', recIntel: 'Arc A750' },
      'GTA V': { minNvidia: 'GTX 660', recNvidia: 'GTX 1660 Super', minAmd: 'HD 7870', recAmd: 'RX 580 8GB', minIntel: 'Arc A380', recIntel: 'Arc A750' },
      'Grand Theft Auto V': { minNvidia: 'GTX 660', recNvidia: 'GTX 1660 Super', minAmd: 'HD 7870', recAmd: 'RX 580 8GB', minIntel: 'Arc A380', recIntel: 'Arc A750' },
      'Far Cry 6': { minNvidia: 'GTX 960', recNvidia: 'RTX 3060', minAmd: 'RX 460', recAmd: 'RX 7600', minIntel: 'Arc A380', recIntel: 'Arc A770' },
      'Fortnite': { minNvidia: 'GTX 960', recNvidia: 'GTX 1660 Super', minAmd: 'R9 280', recAmd: 'RX 580 8GB', minIntel: 'Arc A380', recIntel: 'Arc A750' },
      'Hogwarts Legacy': { minNvidia: 'GTX 960', recNvidia: 'GTX 1060 6GB', minAmd: 'RX 470', recAmd: 'RX 580 8GB', minIntel: 'Arc A750', recIntel: 'Arc A770' },
      'Overwatch 2': { minNvidia: 'GTX 660', recNvidia: 'GTX 1060 6GB', minAmd: 'HD 7950', recAmd: 'R9 380', minIntel: 'Arc A380', recIntel: 'Arc A750' },
      'Rainbow 6 Siege': { minNvidia: 'GTX 460', recNvidia: 'GTX 1660 Super', minAmd: 'HD 7770', recAmd: 'RX 580 8GB', minIntel: 'Arc A310', recIntel: 'Arc A750' },
      'Rainbow Six Siege': { minNvidia: 'GTX 460', recNvidia: 'GTX 1660 Super', minAmd: 'HD 7770', recAmd: 'RX 580 8GB', minIntel: 'Arc A310', recIntel: 'Arc A750' },
      'Tom Clancy\'s Rainbow Six Siege': { minNvidia: 'GTX 460', recNvidia: 'GTX 1660 Super', minAmd: 'HD 7770', recAmd: 'RX 580 8GB', minIntel: 'Arc A310', recIntel: 'Arc A750' },
      'Rocket League': { minNvidia: 'GTX 660', recNvidia: 'GTX 1660 Super', minAmd: 'HD 7870', recAmd: 'RX 580 8GB', minIntel: 'Arc A310', recIntel: 'Arc A750' },
      'Starfield': { minNvidia: 'GTX 1070 Ti', recNvidia: 'RTX 3060 Ti', minAmd: 'RX 5700', recAmd: 'RX 7700 XT', minIntel: 'Arc A770', recIntel: 'Arc B580' },
      'Valorant': { minNvidia: 'GT 730', recNvidia: 'GTX 1050 Ti', minAmd: 'HD 7750', recAmd: 'RX 560', minIntel: 'Arc A310', recIntel: 'Arc A750' }
    }
  };

  /* ==========================================================================
     Hardware Tier & Recommendation Engine (Reusable for CPU & GPU)
     ========================================================================== */
  class HardwareTierEngine {
    constructor(config = {}) {
      this.type = config.type || 'cpu';
      this.brands = config.brands || {};
      this.knownGameRequirements = config.knownGameRequirements || {};
    }

    cleanName(str) {
      if (!str) return '';
      return String(str)
        .replace(/@\s*[\d.]+\s*GHz/gi, '')
        .replace(/\b(Intel|AMD|NVIDIA|GeForce|Radeon|Core|Processor|CPU|Graphics|GPU)\b/gi, ' ')
        .replace(/-/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    parseHardware(str, brandKey = null) {
      if (!str) return null;
      const brandsToSearch = brandKey && this.brands[brandKey] 
        ? [brandKey] 
        : Object.keys(this.brands);

      let normalized = String(str).toLowerCase();
      // Games write cards loosely: "Nvidia 2080 TI" (no RTX), "RX 5600XT" (no space). Put the missing prefix / space back so the
      // names below match - only for model numbers that really exist, so nothing else is reinterpreted.
      if (this.type === 'gpu') {
        normalized = normalized
          .replace(/\b(nvidia|geforce)\s+(?:geforce\s+)?(9[5-9]0|10[3-8]0|16[3-6]0)\b/g, (m, b, n) => `${b} gtx ${n}`)
          .replace(/\b(nvidia|geforce)\s+(?:geforce\s+)?(20[5-8]0|30[5-9]0|40[5-9]0|50[5-9]0)\b/g, (m, b, n) => `${b} rtx ${n}`)
          .replace(/\b(\d{3,4})(xtx|xt|ti|super|gre)\b/g, '$1 $2');
      }

      for (const b of brandsToSearch) {
        const brandData = this.brands[b];
        if (!brandData) continue;
        const safeNorm = normalized.replace(/-/g, ' ');

        // The MOST SPECIFIC name found wins. "RTX 3060" is a substring of "RTX 3060 Ti" (same
        // for 3070/3070 Ti, 4060/4060 Ti, GTX 1660/Super/Ti, RX 7900 XT/XTX ...), and an alias
        // like "geforce rtx 3060" is even longer than "rtx 3060 ti" yet still misses the "Ti".
        // So rank by how far into the text the match reaches (a name that consumed the
        // suffix ends later), then by length, then by tier. First-hit-wins used to silently
        // downgrade the requirement one tier.
        let best = null;
        const consider = (canonical, needle, tierOverride) => {
          const end = safeNorm.lastIndexOf(needle) + needle.length;
          const isPart = tierOverride != null;
          const tier = isPart ? tierOverride : (brandData.tierList ? brandData.tierList.indexOf(canonical) : -1);
          if (!best ||
              end > best.end ||
              (end === best.end && isPart && !best.isPart) ||
              (end === best.end && isPart === best.isPart && needle.length > best.len) ||
              (end === best.end && isPart === best.isPart && needle.length === best.len && tier > best.tier)) {
            best = { canonical, end, len: needle.length, tier, isPart };
          }
        };

        // The parts we actually sell (the same offerings the configurator builds from) are
        // matched by name and use their OWN tier - so a requirement that names one of our
        // cards (e.g. RTX 5070 Ti) resolves to that card instead of falling through to a
        // generic guess.
        (brandData.offerings || []).forEach((o) => {
          const short = String(o.shortName || '').toLowerCase().replace(/-/g, ' ').trim();
          if (!short) return;
          const base = short.replace(/\s*\d+\s*gb$/, '').trim();
          [short, base].forEach((needle) => {
            if (needle.length >= 5 && safeNorm.includes(needle)) consider(o.shortName.replace(/\s*\d+\s*GB$/i, ''), needle, o.tier);
          });
        });

        if (brandData.aliases) {
          for (const [canonical, aliasList] of Object.entries(brandData.aliases)) {
            for (const alias of aliasList) {
              const safeAlias = alias.toLowerCase().replace(/-/g, ' ');
              const regex = new RegExp(`(^|[^a-z0-9])${safeAlias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^a-z0-9]|$)`, 'i');
              if (regex.test(safeNorm) || safeNorm.includes(safeAlias)) consider(canonical, safeAlias);
            }
          }
        }

        if (brandData.tierList) {
          for (const item of brandData.tierList) {
            const safeItem = item.toLowerCase().replace(/-/g, ' ');
            if (safeNorm.includes(safeItem)) consider(item, safeItem);
          }
        }

        if (best) {
          return {
            brand: b,
            brandName: brandData.name || b.toUpperCase(),
            model: best.canonical,
            tier: best.tier >= 0 ? best.tier : 0,
            matchedRaw: best.canonical
          };
        }
      }

      return this.heuristicFallback(str, brandKey);
    }

    heuristicFallback(str, brandKey = null) {
      const text = String(str).toLowerCase();
      if (this.type === 'gpu') {
        if ((!brandKey || brandKey === 'nvidia') && (text.includes('rtx') || text.includes('gtx') || text.includes('geforce') || text.includes('nvidia'))) {
          const m = text.match(/(rtx|gtx)\s*(\d{3,4}(?:\s*ti|\s*super)?)/i);
          if (m) {
            return { brand: 'nvidia', brandName: 'NVIDIA GeForce', model: `${m[1].toUpperCase()} ${m[2].toUpperCase()}`, tier: 40, matchedRaw: m[0] };
          }
        }
        if ((!brandKey || brandKey === 'amd') && (text.includes('radeon') || text.includes('rx') || text.includes('amd'))) {
          const m = text.match(/rx\s*(\d{3,4}(?:\s*xt)?)/i);
          if (m) {
            return { brand: 'amd', brandName: 'AMD Radeon', model: `RX ${m[1].toUpperCase()}`, tier: 30, matchedRaw: m[0] };
          }
        }
        if ((!brandKey || brandKey === 'intel') && text.includes('arc')) {
          const m = text.match(/arc\s*([ab]\d{3})/i);
          if (m) {
            return { brand: 'intel', brandName: 'Intel Arc', model: `Arc ${m[1].toUpperCase()}`, tier: 3, matchedRaw: m[0] };
          }
        }
        return null;
      }

      // CPU heuristic
      if ((!brandKey || brandKey === 'intel') && (text.includes('intel') || /i[3579]\b/.test(text))) {
        const m = text.match(/i([3579])[ -]?(\d{4,5})/i);
        if (m) {
          const series = parseInt(m[1], 10);
          const modelNum = parseInt(m[2], 10);
          let tier = 0;
          if (series === 3) tier = 0;
          else if (series === 5) {
            if (modelNum < 12000) tier = 0;
            else if (modelNum < 13000) tier = 2;
            else tier = 4;
          } else if (series === 7) {
            if (modelNum < 12000) tier = 1;
            else if (modelNum < 14000) tier = 3;
            else tier = 5;
          } else if (series === 9) {
            tier = 6;
          }
          return {
            brand: 'intel',
            brandName: 'Intel',
            model: `i${series} ${modelNum}`,
            tier,
            matchedRaw: m[0]
          };
        }
      }

      if ((!brandKey || brandKey === 'amd') && (text.includes('amd') || text.includes('ryzen'))) {
        const m = text.match(/ryzen\s*([3579])?\s*(\d{4}[a-z0-9]*)/i);
        if (m) {
          const modelNum = parseInt(m[2], 10);
          const isX3D = /x3d/i.test(text);
          let tier = 0;
          if (isX3D) {
            if (modelNum >= 9000) tier = 9;
            else if (modelNum >= 7900) tier = 10;
            else tier = 5;
          } else if (modelNum < 5500) {
            tier = 0;
          } else if (modelNum < 5700) {
            tier = 1;
          } else if (modelNum < 7000) {
            tier = 2;
          } else if (modelNum < 9000) {
            tier = 3;
          } else {
            tier = 4;
          }
          return {
            brand: 'amd',
            brandName: 'AMD Ryzen',
            model: `Ryzen ${m[2].toUpperCase()}`,
            tier,
            matchedRaw: m[0]
          };
        }
      }

      return null;
    }

    mapAmdToNvidiaTier(tier, model = '') {
      const u = (model || '').toUpperCase();
      if (/HD\s*7\d|R7|R9\s*2\d|R9\s*380|RX\s*4\d|RX\s*5[5-9]0|RX\s*6400|RX\s*6500/i.test(u)) return 42;
      if (/RX\s*5500\s*XT|VEGA\s*56|RX\s*5600/i.test(u)) return 48;
      if (/VEGA\s*64|RX\s*5700|RX\s*6600(?!\s*XT)/i.test(u)) return 53;
      if (/RX\s*6600\s*XT|RX\s*6650|RX\s*7600(?!\s*XT)/i.test(u)) return 57;
      if (/RX\s*6700|RX\s*6750|RX\s*7600\s*XT/i.test(u)) return 60;
      if (/RX\s*6800(?!\s*XT)|RX\s*7700/i.test(u)) return 61;
      if (/RX\s*6800\s*XT|RX\s*7800/i.test(u)) return 64;
      if (/RX\s*6900|RX\s*6950|RX\s*7900\s*GRE|RX\s*9070(?!\s*XT)/i.test(u)) return 65;
      if (/RX\s*7900\s*XT|RX\s*7900\s*XTX|RX\s*9070\s*XT/i.test(u)) return 71;
      const amdTotal = this.brands.amd && this.brands.amd.tierList ? this.brands.amd.tierList.length : 46;
      const score = tier / Math.max(1, amdTotal - 1);
      return Math.min(74, Math.round(score * 74));
    }

    mapArcToNvidiaTier(tier, model = '') {
      const u = (model || '').toUpperCase();
      if (/A310|A380|A580|A750/i.test(u)) return 42;
      if (/A770/i.test(u)) return 48;
      if (/B580/i.test(u)) return 53;
      return 42;
    }

    // The least video memory (GB) a game asks for at one level, 0 when it names none: "at least 8GB of DEDICATED memory",
    // "VRAM: 4 GB", or a separate video-memory row. A card's own name ("RX 580 8GB") is NOT a floor - its tier covers that.
    vramFloor(game, specType = 'recommended') {
      if (this.type !== 'gpu' || !game || !game.requirements) return 0;
      const rows = game.requirements[specType + 'Rows'] || game.requirements[specType];
      const texts = [];
      if (Array.isArray(rows)) {
        rows.forEach((r) => { if (r && (r.key === 'gpu' || r.key === 'vram')) texts.push({ key: r.key, text: String(r.value || '') }); });
      } else if (rows && typeof rows === 'object') {
        if (rows.gpu) texts.push({ key: 'gpu', text: String(rows.gpu) });
        if (rows.vram) texts.push({ key: 'vram', text: String(rows.vram) });
      }
      const toGb = (n, unit) => (/mb/i.test(unit) ? parseFloat(n) / 1024 : parseFloat(n));
      // A bare "memory" is not enough: Steam rows run together ("... R9 390 8GB Memory 8 GB RAM" is the next row's label). The
      // word has to be qualified (video / graphics / gpu / dedicated / discrete memory, VRAM) or follow "of" ("6GB of memory").
      const qual = '(?:video\\s+|graphics\\s+|gpu\\s+|dedicated\\s+|discrete\\s+)';
      const memQual = `(?:${qual}+(?:memory|ram)|vram|video\\s+ram)`;
      const num = '(\\d+(?:\\.\\d+)?)\\s*(gb|mb)';
      const after = new RegExp(`${num}\\s*\\+?\\s*(?:of\\s+${qual}*(?:memory|vram|video\\s+ram)|${memQual})`, 'i');
      const before = new RegExp(`${memQual}\\s*(?:[:=-]|of|at\\s+least|min(?:imum)?)?\\s*${num}`, 'i');
      let best = 0;
      texts.forEach(({ key, text }) => {
        let m = text.match(after) || text.match(before);
        if (!m && key === 'vram') m = text.match(/(\d+(?:\.\d+)?)\s*(gb|mb)/i);
        if (m) best = Math.max(best, toGb(m[1], m[2]));
      });
      return best;
    }

    getGameHardware(game, specType = 'recommended') {
      const hw = this.gameHardwareTiers(game, specType);
      if (this.type === 'gpu') hw.vramGb = this.vramFloor(game, specType);
      return hw;
    }

    gameHardwareTiers(game, specType = 'recommended') {
      const gName = (game.name || '').trim();
      const isGpu = this.type === 'gpu';

      for (const [title, reqs] of Object.entries(this.knownGameRequirements)) {
        if (gName.toLowerCase() === title.toLowerCase() || gName.toLowerCase().includes(title.toLowerCase())) {
          if (isGpu) {
            const nvKey = specType === 'minimum' ? 'minNvidia' : 'recNvidia';
            const amdKey = specType === 'minimum' ? 'minAmd' : 'recAmd';
            const nvModel = reqs[nvKey];
            const amdModel = reqs[amdKey];

            const nvTier = this.brands.nvidia && nvModel ? this.brands.nvidia.tierList.indexOf(nvModel) : 0;
            const amdTier = this.brands.amd && amdModel ? this.brands.amd.tierList.indexOf(amdModel) : 0;

            return {
              gameName: gName,
              rawSpec: `${nvModel || ''} / ${amdModel || ''}`.replace(/^\s*\/\s*|\s*\/\s*$/g, ''),
              primary: { model: nvModel || 'GTX 1660 Super', tier: Math.max(0, nvTier), brand: 'nvidia' },
              secondary: { model: amdModel || 'RX 580 8GB', tier: Math.max(0, amdTier), brand: 'amd' }
            };
          } else {
            const intelKey = specType === 'minimum' ? 'minIntel' : 'recIntel';
            const ryzenKey = specType === 'minimum' ? 'minRyzen' : 'recRyzen';
            const intelModel = reqs[intelKey];
            const ryzenModel = reqs[ryzenKey];

            const intelTier = this.brands.intel && intelModel ? this.brands.intel.tierList.indexOf(intelModel) : 0;
            const ryzenTier = this.brands.amd && ryzenModel ? this.brands.amd.tierList.indexOf(ryzenModel) : 0;

            return {
              gameName: gName,
              rawSpec: `${intelModel || ''} / ${ryzenModel || ''}`.replace(/^\s*\/\s*|\s*\/\s*$/g, ''),
              primary: { model: intelModel || 'i5 7500', tier: Math.max(0, intelTier), brand: 'intel' },
              secondary: { model: ryzenModel || 'Ryzen 4500', tier: Math.max(0, ryzenTier), brand: 'amd' }
            };
          }
        }
      }

      let rawText = '';
      if (game.requirements && game.requirements[specType]) {
        const key = this.type;
        const rows = game.requirements[specType + 'Rows'] || game.requirements[specType];
        if (Array.isArray(rows)) {
          const row = rows.find(r => r.key === key);
          if (row) rawText = row.value || '';
        } else if (typeof rows === 'object' && rows[key]) {
          rawText = rows[key];
        }
      }

      if (isGpu) {
        let nvMatch = this.parseHardware(rawText, 'nvidia');
        let amdMatch = this.parseHardware(rawText, 'amd');
        let arcMatch = this.parseHardware(rawText, 'intel');

        if (!nvMatch) {
          if (amdMatch) {
            const mappedNvTier = this.mapAmdToNvidiaTier(amdMatch.tier, amdMatch.model);
            nvMatch = { model: amdMatch.model, tier: mappedNvTier, brand: 'nvidia' };
          } else if (arcMatch) {
            const mappedNvTier = this.mapArcToNvidiaTier(arcMatch.tier, arcMatch.model);
            nvMatch = { model: arcMatch.model, tier: mappedNvTier, brand: 'nvidia' };
          }
        }

        return {
          gameName: gName,
          rawSpec: rawText || 'Standard Baseline Graphics',
          primary: nvMatch ? { model: nvMatch.model, tier: nvMatch.tier, brand: 'nvidia' } : { model: 'GTX 1660 Super', tier: 42, brand: 'nvidia' },
          secondary: amdMatch ? { model: amdMatch.model, tier: amdMatch.tier, brand: 'amd' } : { model: 'RX 580 8GB', tier: 27, brand: 'amd' }
        };
      }

      const intelMatch = this.parseHardware(rawText, 'intel');
      const amdMatch = this.parseHardware(rawText, 'amd');

      return {
        gameName: gName,
        rawSpec: rawText || 'Standard Baseline Requirements',
        primary: intelMatch ? { model: intelMatch.model, tier: intelMatch.tier, brand: 'intel' } : { model: 'i5 7500', tier: 0, brand: 'intel' },
        secondary: amdMatch ? { model: amdMatch.model, tier: amdMatch.tier, brand: 'amd' } : { model: 'Ryzen 4500', tier: 0, brand: 'amd' }
      };
    }

    findPeak(games, specType = 'recommended') {
      if (!games || !games.length) return null;

      let peakPrimary = { tier: -1, model: '', gameName: '', rawSpec: '' };
      let peakSecondary = { tier: -1, model: '', gameName: '', rawSpec: '' };
      let peakOverallGame = games[0].name || '';
      let peakOverallRaw = '';
      let peakVramGb = 0;

      for (const game of games) {
        const req = this.getGameHardware(game, specType);
        if (req.vramGb > peakVramGb) peakVramGb = req.vramGb;
        if (req.primary && req.primary.tier > peakPrimary.tier) {
          peakPrimary = {
            tier: req.primary.tier,
            model: req.primary.model,
            gameName: req.gameName,
            rawSpec: req.rawSpec
          };
        }
        if (req.secondary && req.secondary.tier > peakSecondary.tier) {
          peakSecondary = {
            tier: req.secondary.tier,
            model: req.secondary.model,
            gameName: req.gameName,
            rawSpec: req.rawSpec
          };
        }
      }

      const defaultPrim = this.type === 'gpu' ? 'GTX 1660 Super' : 'i5 7500';
      const defaultSec = this.type === 'gpu' ? 'RX 580 8GB' : 'Ryzen 4500';

      if (peakPrimary.tier < 0) peakPrimary = { tier: 0, model: defaultPrim, gameName: games[0].name || '', rawSpec: 'Baseline Spec' };
      if (peakSecondary.tier < 0) peakSecondary = { tier: 0, model: defaultSec, gameName: games[0].name || '', rawSpec: 'Baseline Spec' };

      peakOverallGame = peakPrimary.tier >= 2 || peakSecondary.tier >= 2
        ? (peakSecondary.tier >= 4 ? peakSecondary.gameName : peakPrimary.gameName)
        : games[0].name || '';
      peakOverallRaw = peakPrimary.gameName === peakOverallGame ? peakPrimary.rawSpec : peakSecondary.rawSpec;

      return {
        gameName: peakOverallGame,
        rawSpec: peakOverallRaw || `${peakPrimary.model} / ${peakSecondary.model}`,
        primary: peakPrimary,
        secondary: peakSecondary,
        vramGb: peakVramGb,
        intel: this.type === 'cpu' ? peakPrimary : null,
        amd: this.type === 'cpu' ? peakSecondary : peakSecondary,
        nvidia: this.type === 'gpu' ? peakPrimary : null
      };
    }

    // Picks the cheapest real catalog offering that meets reqTier. With boost:true,
    // steps one entry further up the SAME sorted offerings list (still a real, sellable
    // part from our own catalog — never a hallucinated/synthesized one).
    // minVramGb (GPU only): a card needs the tier AND at least that much video memory - the 6 GB 1660 SUPER never answers a
    // "8GB of dedicated memory" requirement. A card with no readable memory size is not held back.
    pickOffering(offerings, reqTier, boost = false, minVramGb = 0) {
      if (!offerings || !offerings.length) return null;
      const offs = [...offerings].sort((a, b) => a.tier - b.tier);
      const vramOk = (o) => !minVramGb || specGb(o.vram) == null || specGb(o.vram) >= minVramGb;
      let idx = offs.findIndex(o => o.tier >= reqTier && vramOk(o));
      if (idx === -1) idx = offs.length - 1;
      if (boost && idx < offs.length - 1) idx += 1;
      return offs[idx];
    }

    getRecommendations(peakReq, boost = false) {
      if (!peakReq) return null;

      const primReqTier = peakReq.primary ? peakReq.primary.tier : 0;
      const secReqTier = peakReq.secondary ? peakReq.secondary.tier : 0;

      const isGpu = this.type === 'gpu';

      if (isGpu) {
        const vramGb = peakReq.vramGb || 0;
        const recNvidia = this.pickOffering(this.brands.nvidia && this.brands.nvidia.offerings, primReqTier, boost, vramGb);
        const recAmd = this.pickOffering(this.brands.amd && this.brands.amd.offerings, secReqTier, boost, vramGb);
        const primary = recNvidia || (this.brands.nvidia && this.brands.nvidia.offerings ? this.brands.nvidia.offerings[0] : null);

        return {
          nvidia: recNvidia,
          amd: recAmd,
          primary: primary
        };
      }

      // CPU recommendations
      const recIntel = this.pickOffering(this.brands.intel && this.brands.intel.offerings, primReqTier, boost);
      const recAmd = this.pickOffering(this.brands.amd && this.brands.amd.offerings, secReqTier, boost);

      const primary = (recAmd && recAmd.tier >= 5) ? recAmd : (recAmd || recIntel);

      return {
        intel: recIntel,
        amd: recAmd,
        primary: primary
      };
    }

    evaluate(games, tierMode = 'recommended') {
      const peakMin = this.findPeak(games, 'minimum');
      const peakRec = this.findPeak(games, 'recommended');
      const recommendations = this.getRecommendationsForTier(games, tierMode);

      return {
        type: this.type,
        peakMinimum: peakMin,
        peakRecommended: peakRec,
        recommendations: recommendations
      };
    }

    // tierMode: 'minimum' | 'recommended' | 'boost'. Boost reuses the recommended peak
    // requirement but picks the next real offering up from what recommended would pick —
    // per spec, never a separate/higher requirement tier.
    getRecommendationsForTier(games, tierMode = 'recommended') {
      const peakMin = this.findPeak(games, 'minimum');
      const peakRec = this.findPeak(games, 'recommended');
      if (tierMode === 'minimum') {
        return this.getRecommendations(peakMin || peakRec, false);
      }
      return this.getRecommendations(peakRec || peakMin, tierMode === 'boost');
    }
  }

  /* ======================================================================== */

  /* ==========================================================================
     Requirements check (owner 2026-10-04): does the shopper's CURRENT PC meet a game's minimum / recommended specs?
     Used by the product-page searchbar (<gbg-build-finder data-mode="product">). It runs on the SAME engines and data as the
     recommendation above (HardwareTierEngine, gbg-cpu-tiers.json, gbg-gpu-tiers.json, the live catalog's offerings), so
     "meets" and "we recommend" can never disagree: the part we recommend for a requirement is the cheapest one that meets it.

       pc = { cpu: 'Ryzen 7800X3D', gpu: 'RTX 5070 12GB', ramGb: 32 }       (what the page says the shopper has picked)
       checkRequirements(game, pc, cpuEngine, gpuEngine)
         -> { minimum: spec, recommended: spec }
       spec = { verdict: 'meets' | 'below' | 'unknown' | 'none', exceeds: boolean, rows: [{ key, label, need, yours, status, above }] }
              status: 'meets' | 'below' | 'unknown' ("can't verify automatically" - never shown as a pass)
              above (CPU and GPU rows only): the shopper's part is strictly better than the requirement (a higher tier, or more GHz / cores / video memory)
              exceeds: verdict is 'meets' AND the CPU and GPU rows are all above - the popup says "Your PC Exceeds This"; otherwise "Meets This".
                       Memory, video memory and anything else never decide meets-vs-exceeds (owner 2026-10-04).
              'none' = the game lists nothing for that level.
     A listing is only passed when we can really read it: a part the tier lists know, a part from before those lists start
     (Core 2, GeForce 8000, Radeon HD 2000-6000: far below anything we sell), a plain clock speed / core count, or a video memory
     size. Anything else ("Ryzen 5 CPU or equivalent") stays "unknown".
     ========================================================================== */
  const ROW_LABELS = { cpu: 'Processor', gpu: 'Graphics', ram: 'Memory', vram: 'Video memory' };
  // hardware older than the tier lists (they start at GTX 460 / HD 7750 / i5 7500): every PC we sell is far above it
  const LEGACY_CPU = /core\s*2\s*(?:duo|quad)|athlon|pentium|phenom|celeron|\bi[357][\s-]*\d{3}\b/i;
  const LEGACY_GPU = [/(?:nvidia|geforce)\s*(?:geforce\s*)?[6-9]\d{3}\b/i, /\bhd\s*[2-6]\d{3}\b/i, /\bati\b[^,;]*\b[2-5]\d{3}\s*series/i];
  const NAMED_CORES = { dual: 2, quad: 4, hexa: 6, six: 6, octa: 8, eight: 8 };

  // "12 GB RAM" -> 12, "512 MB" -> 0.5, anything else -> null
  const specGb = (text) => {
    const m = text && String(text).match(/(\d+(?:\.\d+)?)\s*(GB|MB)\b/i);
    if (!m) return null;
    return /mb/i.test(m[2]) ? parseFloat(m[1]) / 1024 : parseFloat(m[1]);
  };
  // RAM we pair with a requirement (owner 2026-10-08): the build bar offers 16 GB and 32 GB. 16 GB is the least we sell and covers
  // anything up to 16; a game that asks for more gets 32 (the most the bar offers). Performance Boost steps up to 32.
  const RAM_CHOICES = [16, 32];
  const ramKitFor = (needGb) => RAM_CHOICES.find((gb) => gb >= (needGb || 0)) || RAM_CHOICES[RAM_CHOICES.length - 1];
  // what a game lists as RAM at one level (GB), 0 when it lists none
  const ramNeedOf = (game, spec) => specGb(reqText(game, spec, 'ram')) || 0;
  // the kit for the games picked: the toughest game's RAM at that level
  function recommendedRamGb(games, tierMode) {
    if (tierMode === 'boost') return RAM_CHOICES[RAM_CHOICES.length - 1];
    const spec = tierMode === 'minimum' ? 'minimum' : 'recommended';
    return ramKitFor(Math.max(0, ...(games || []).map((g) => ramNeedOf(g, spec))));
  }
  // the lowest clock a listing names ("Core i5 @ 3.3 GHz or AMD Ryzen 3 @ 3.1 GHz" -> 3.1: either one will do)
  const specGhz = (text) => {
    const all = (String(text || '').match(/(\d+(?:\.\d+)?)\s*ghz/gi) || []).map((s) => parseFloat(s));
    return all.length ? Math.min(...all) : null;
  };
  const coresIn = (text) => {
    const s = String(text || '').toLowerCase();
    let m = s.match(/\b(dual|quad|hexa|six|octa|eight)[\s-]*core/);
    if (m) return NAMED_CORES[m[1]];
    m = s.match(/(\d+)[\s-]*cores?\b/);
    if (m) return parseInt(m[1], 10);
    m = s.match(/(\d+)\s*(?:hardware\s*)?(?:cpu\s*)?threads?\b/);
    return m ? Math.ceil(parseInt(m[1], 10) / 2) : null;
  };

  // The text a game lists for one part at one level ('minimum' | 'recommended'), '' when it lists none.
  const reqText = (game, spec, key) => {
    const rows = game && game.requirements && game.requirements[spec];
    if (!Array.isArray(rows)) return '';
    const row = rows.find((r) => r && r.key === key);
    return row ? String(row.value || '') : '';
  };

  // Titles the engines have hand-checked tables for (the same test getGameHardware uses).
  const isKnownGame = (engine, game) => {
    const name = String((game && game.name) || '').trim().toLowerCase();
    return !!name && Object.keys(engine.knownGameRequirements || {}).some((t) => name === t.toLowerCase() || name.includes(t.toLowerCase()));
  };

  // Part names compared without the noise: "Ryzen 7 7800X3D" = "Ryzen 7800X3D", "Core i5-12400F" = "Core i5 12400F".
  const normPart = (s) => String(s || '').toLowerCase().replace(/-/g, ' ').replace(/\bryzen\s+\d\s+/g, 'ryzen ').replace(/\s+/g, ' ').trim();

  // The catalog offering named like `name` (the longest match wins), with its brand. null when it is not one we sell.
  // "RTX 5060" is the 5060 8GB offering; "RTX 3060 Ti 8GB" is NOT the 3060 (what follows the name must be nothing or a size).
  const offeringOf = (engine, name) => {
    const lo = normPart(name);
    let best = null;
    Object.keys(engine.brands || {}).forEach((brand) => {
      ((engine.brands[brand] && engine.brands[brand].offerings) || []).forEach((o) => {
        const short = normPart(o.shortName);
        const base = short.replace(/\s*\d+\s*gb$/, '').trim();
        [short, base].forEach((needle) => {
          const at = needle ? lo.indexOf(needle) : -1;
          if (at < 0) return;
          const rest = lo.slice(at + needle.length).trim();
          if (rest && !/^\d+\s*gb\b/.test(rest)) return;
          if (!best || needle.length > best.len) best = { brand, offering: o, len: needle.length };
        });
      });
    });
    return best;
  };

  // The shopper's GPU on the NVIDIA tier scale (AMD and Arc cards are mapped the way the recommendation maps them).
  function pcGpuInfo(gpuEngine, name) {
    if (!name) return null;
    const hit = gpuEngine.parseHardware(name);
    if (!hit) return null;
    const tier = hit.brand === 'amd' ? gpuEngine.mapAmdToNvidiaTier(hit.tier, hit.model)
      : hit.brand === 'intel' ? gpuEngine.mapArcToNvidiaTier(hit.tier, hit.model) : hit.tier;
    const own = offeringOf(gpuEngine, name);
    return { tier, vramGb: own ? specGb(own.offering.vram) : null };
  }

  // The shopper's CPU: its brand, its place in that brand's tier list, and (when we sell it) its core count and boost clock.
  function pcCpuInfo(cpuEngine, name) {
    if (!name) return null;
    const hit = cpuEngine.parseHardware(name);
    if (!hit || (hit.brand !== 'intel' && hit.brand !== 'amd')) return null;
    const own = offeringOf(cpuEngine, name);
    return { brand: hit.brand, tier: hit.tier, cores: own ? own.offering.cores || null : null, ghz: own ? specGhz(own.offering.turbo) : null };
  }

  // A CPU listing no tier list knows: very old hardware passes; a clock / core count is compared with the shopper's CPU.
  // -> true | false | null (null = can't tell)
  function genericCpuMeets(text, mine) {
    if (LEGACY_CPU.test(text)) return true;
    const ghz = specGhz(text);
    const cores = coresIn(text);
    if (ghz == null && cores == null) return null;
    if ((ghz != null && mine.ghz == null) || (cores != null && mine.cores == null)) return null;
    return (ghz == null || mine.ghz >= ghz) && (cores == null || mine.cores >= cores);
  }

  // The same listing, but is the shopper's CPU strictly better on everything the listing names? (very old hardware: always)
  function genericCpuAbove(text, mine) {
    if (LEGACY_CPU.test(text)) return true;
    const ghz = specGhz(text);
    const cores = coresIn(text);
    if (ghz == null && cores == null) return false;
    return (ghz == null || (mine.ghz != null && mine.ghz > ghz)) && (cores == null || (mine.cores != null && mine.cores > cores));
  }

  // One CPU row. The requirement may name an Intel part, an AMD part or both: compare in the shopper's own brand; when only the
  // other brand is named, convert by position in the tier lists (the same percentile idea the recommendation uses).
  function checkCpuRow(game, spec, pc, cpuEngine) {
    const text = reqText(game, spec, 'cpu');
    const known = isKnownGame(cpuEngine, game);
    if (!text && !known) return null;
    const need = known ? (cpuEngine.getGameHardware(game, spec).rawSpec || text) : text;
    const row = { key: 'cpu', label: ROW_LABELS.cpu, need, yours: pc.cpu || '', status: 'unknown', above: false };
    const mine = pcCpuInfo(cpuEngine, pc.cpu);
    if (!mine) return row;
    const intelHit = known ? true : !!cpuEngine.parseHardware(text, 'intel');
    const amdHit = known ? true : !!cpuEngine.parseHardware(text, 'amd');
    if (!intelHit && !amdHit) {
      const ok = genericCpuMeets(text, mine);
      if (ok != null) row.status = ok ? 'meets' : 'below';
      row.above = ok === true && genericCpuAbove(text, mine);
      return row;
    }
    const gh = cpuEngine.getGameHardware(game, spec);
    const listLen = (b) => ((cpuEngine.brands[b] && cpuEngine.brands[b].tierList) || []).length;
    const fromIntel = gh.primary ? gh.primary.tier : 0;
    const fromAmd = gh.secondary ? gh.secondary.tier : 0;
    let required;
    if (mine.brand === 'intel') {
      required = intelHit ? fromIntel : Math.round((fromAmd / Math.max(1, listLen('amd') - 1)) * Math.max(1, listLen('intel') - 1));
    } else {
      required = amdHit ? fromAmd : Math.round((fromIntel / Math.max(1, listLen('intel') - 1)) * Math.max(1, listLen('amd') - 1));
    }
    row.status = mine.tier >= required ? 'meets' : 'below';
    row.above = mine.tier > required; // a higher place in the tier list than the requirement's
    return row;
  }

  // One GPU row (NVIDIA tier scale). A listing with no card we know: old cards pass, a memory size ("1 GB or more") is checked
  // against the shopper's video memory.
  function checkGpuRow(game, spec, pc, gpuEngine) {
    const text = reqText(game, spec, 'gpu');
    const known = isKnownGame(gpuEngine, game);
    if (!text && !known) return null;
    const need = known ? (gpuEngine.getGameHardware(game, spec).rawSpec || text) : text;
    const row = { key: 'gpu', label: ROW_LABELS.gpu, need, yours: pc.gpu || '', status: 'unknown', above: false };
    const mine = pcGpuInfo(gpuEngine, pc.gpu);
    if (!mine) return row;
    if (known || gpuEngine.parseHardware(text)) {
      const required = gpuEngine.getGameHardware(game, spec).primary.tier;
      row.status = mine.tier >= required ? 'meets' : 'below';
      row.above = mine.tier > required;
      // the game also asks for an amount of video memory ("at least 8GB of dedicated memory"): the card needs it too
      const floor = gpuEngine.vramFloor(game, spec);
      if (floor && row.status === 'meets') row.status = mine.vramGb == null ? 'unknown' : mine.vramGb >= floor ? 'meets' : 'below';
      return row;
    }
    if (LEGACY_GPU.some((re) => re.test(text))) {
      row.status = 'meets';
      row.above = true;
      return row;
    }
    const gb = specGb(text);
    if (gb != null && mine.vramGb != null) {
      row.status = mine.vramGb >= gb ? 'meets' : 'below';
      row.above = mine.vramGb > gb;
    }
    return row;
  }

  function checkRequirements(game, pc, cpuEngine, gpuEngine) {
    const out = {};
    ['minimum', 'recommended'].forEach((spec) => {
      const rows = [];
      const cpu = checkCpuRow(game, spec, pc, cpuEngine);
      const gpu = checkGpuRow(game, spec, pc, gpuEngine);
      if (cpu) rows.push(cpu);
      if (gpu) rows.push(gpu);
      const ramText = reqText(game, spec, 'ram');
      const ramNeed = specGb(ramText);
      if (ramText) {
        const known = ramNeed != null && !!pc.ramGb;
        rows.push({ key: 'ram', label: ROW_LABELS.ram, need: ramText, yours: pc.ramGb ? pc.ramGb + ' GB' : '',
          status: !known ? 'unknown' : pc.ramGb >= ramNeed ? 'meets' : 'below' });
      }
      const vramText = reqText(game, spec, 'vram');
      const vramNeed = specGb(vramText);
      if (vramText) {
        const mine = pcGpuInfo(gpuEngine, pc.gpu);
        const known = vramNeed != null && !!mine && mine.vramGb != null;
        rows.push({ key: 'vram', label: ROW_LABELS.vram, need: vramText, yours: mine && mine.vramGb ? mine.vramGb + ' GB' : '',
          status: !known ? 'unknown' : mine.vramGb >= vramNeed ? 'meets' : 'below' });
      }
      let verdict = 'none';
      if (rows.length) verdict = rows.some((r) => r.status === 'below') ? 'below' : rows.some((r) => r.status === 'unknown') ? 'unknown' : 'meets';
      // "exceeds" (owner 2026-10-04): the PC passes AND its CPU and its GPU are both strictly better than the game's - memory,
      // video memory, storage and everything else are NOT part of that choice; otherwise just "meets". A game that lists only
      // one of the two is judged on that one; one that lists neither can only "meet".
      const core = rows.filter((r) => r.key === 'cpu' || r.key === 'gpu');
      out[spec] = { verdict, rows, exceeds: verdict === 'meets' && core.length > 0 && core.every((r) => r.above) };
    });
    return out;
  }

  // The line the popup shows under each column's title (owner 2026-10-04): "Your PC Meets This" / "Your PC Exceeds This".
  // A PC below a requirement, or one we can't fully read, says so in the same place - never a pass. -> { tone, text } | null
  function verdictLine(result) {
    if (!result || result.verdict === 'none') return null;
    if (result.verdict === 'meets') return { tone: 'good', text: result.exceeds ? 'Your PC Exceeds This' : 'Your PC Meets This' };
    if (result.verdict === 'below') {
      const short = result.rows.filter((r) => r.status === 'below').map((r) => r.label);
      return { tone: 'short', text: 'Your PC Falls Short' + (short.length ? ': ' + short.join(', ') : '') };
    }
    return { tone: 'unknown', text: 'We Can’t Fully Check This' };
  }

  // What we recommend for a game at one level: the same parts the by-game page recommends (AMD CPU + NVIDIA GPU + RAM).
  // -> { cpu: offering|null, gpu: offering|null, ramGb: 16|32, text: 'Ryzen 5 5500 + GTX 1660 SUPER + 16GB RAM' }
  function recommendFor(game, spec, cpuEngine, gpuEngine) {
    const c = cpuEngine.getRecommendationsForTier([game], spec) || {};
    const g = gpuEngine.getRecommendationsForTier([game], spec) || {};
    const cpu = c.amd || c.primary || null;
    const gpu = g.nvidia || g.primary || null;
    const ramGb = recommendedRamGb([game], spec);
    const nm = (o) => (o ? (o.shortName || o.name) : '');
    return { cpu, gpu, ramGb, text: [nm(cpu), nm(gpu), `${ramGb}GB RAM`].filter(Boolean).join(' + ') };
  }

  // "CPU: ...; GPU: ...; RAM: ..." - the specs a game lists at one level, as logged for the dashboard.
  function specLine(game, spec) {
    const rows = (game && game.requirements && game.requirements[spec]) || [];
    const pick = (key) => (Array.isArray(rows) ? rows.find((r) => r && r.key === key) : null);
    return [['CPU', 'cpu'], ['GPU', 'gpu'], ['RAM', 'ram']].map(([label, key]) => {
      const r = pick(key);
      return r && r.value ? `${label}: ${r.value}` : '';
    }).filter(Boolean).join('; ');
  }

  class GbgBuildFinder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      const d = this.dataset;
      this.sectionId = d.sectionId || this.id;
      this.steamProxy = (d.steamProxy || '').trim() || STEAM_PROXY;
      this.trendingOn = d.trending === 'true';
      this.trendingCount = parseInt(d.trendingCount, 10) || 12;
      this.labels = { add: d.labelAdd || 'Add to my games', remove: d.labelRemove || 'Remove from my games' };
      // data-mode="product": the finder on a product page. Everything the by-game page does (games, performance level, CPU / GPU /
      // RAM, the recommendation) except the case, which is the page's own; the popup also says how the shopper's PC compares; and
      // the button gives the recommended parts to the configurator on the same page (window.GBG_configurator) instead of building
      // a new product.
      this.productMode = d.mode === 'product';
      if (this.productMode && !document.getElementById('gbg-configurator')) {
        // product mode works the configurator on this page; without one it would wait forever ("Calculating...")
        console.error('[gbg finder] this page has no configurator - the game finder is hidden');
        this.hidden = true;
        return;
      }

      this.registry = new Map();
      this.selected = new Map();
      this.caseSel = null;
      this.tierSelection = 'recommended';
      this.selectedCpuBrand = 'amd';
      this.selectedGpuBrand = 'nvidia';
      // RAM kit (16 / 32 GB): null = follow the picked games, a number = the shopper's own choice
      this.ramChosen = null;
      this.searchSeq = 0;
      this.popularList = [];

      const q = (sel) => this.querySelector(sel);
      this.ui = {
        searchInput: q('[data-gbgb-search-input]'),
        searchClear: q('[data-gbgb-search-clear]'),
        results: q('[data-gbgb-results]'),
        resultsTitle: q('[data-gbgb-results-title]'),
        resultsNote: q('[data-gbgb-results-note]'),
        resultsGrid: q('[data-gbgb-results-grid]'),
        browse: q('[data-gbgb-browse]'),
        popular: q('[data-gbgb-popular]'),
        popularGrid: q('[data-gbgb-popular-grid]'),
        morePopular: q('[data-gbgb-more="popular"]'),
        trending: q('[data-gbgb-trending]'),
        trendingGrid: q('[data-gbgb-trending-grid]'),
        moreTrending: q('[data-gbgb-more="trending"]'),
        chips: q('[data-gbgb-chips]'),
        count: q('[data-gbgb-count]'),
        empty: q('[data-gbgb-empty]'),
        rails: this.querySelectorAll('[data-gbgb-cases]'),
        tierTabs: this.querySelectorAll('[data-gbgb-tier-btn]'),
        tierDesc: q('[data-gbgb-tier-desc]'),
        cpuTabs: this.querySelectorAll('[data-gbgb-cpu-btn]'),
        gpuTabs: this.querySelectorAll('[data-gbgb-gpu-btn]'),
        ramTabs: this.querySelectorAll('[data-gbgb-ram-btn]'),
        debug: q('[data-gbgb-debug]'),
        debugMin: q('[data-gbgb-debug-min]'),
        debugRec: q('[data-gbgb-debug-rec]'),
        debugFinal: q('[data-gbgb-debug-final]'),
        summary: q('[data-gbgb-summary]'),
        error: q('[data-gbgb-error]'),
        go: q('[data-gbgb-go]'),
        loadingOverlay: q('[data-gbgb-loading-overlay]'),
        loadingCaption: q('[data-gbgb-loading-caption]'),
        live: q('[data-gbgb-live]'),
        dialog: q('[data-gbgb-dialog]'),
        dialogTitle: q('[data-gbgb-dialog-title]'),
        dialogYear: q('[data-gbgb-dialog-year]'),
        dialogBody: q('[data-gbgb-dialog-body]'),
        dialogToggle: q('[data-gbgb-dialog-toggle]'),
        dialogLink: q('[data-gbgb-dialog-link]'),
        dialogMedia: q('[data-gbgb-dialog-media]'),
        dialogSource: q('[data-gbgb-dialog-source]'),
        recDialog: q('[data-gbgb-rec-dialog]'),
        recTitle: q('[data-gbgb-rec-title]'),
        recSub: q('[data-gbgb-rec-sub]'),
        recBody: q('[data-gbgb-rec-body]'),
        recClose: q('[data-gbgb-rec-close]'),
        recBack: q('[data-gbgb-rec-back]'),
        recProceed: q('[data-gbgb-rec-proceed]'),
        step1: q('#GbgbStep1-' + this.sectionId),
        step2: q('#GbgbStep2-' + this.sectionId),
      };

      // by-game page: 12 (6 on a phone). Product page (owner 2026-10-12): ONE row of Popular - as many tiles as the grid has columns - and 4 on a phone
      this.getBatchSize = () => {
        if (!this.productMode) return window.innerWidth < 768 ? 6 : 12;
        if (window.innerWidth < 768) return 4;
        return window.innerWidth >= 1140 ? 6 : window.innerWidth >= 900 ? 4 : 3; // the grid's own columns at these widths
      };
      this.batchSize = this.getBatchSize();
      this.popularList = [];
      this.popularShown = 0;
      this.popularClicks = 0;

      this.trendingList = [];
      this.trendingShown = 0;
      this.trendingClicks = 0;

      if (!this.productMode) this.restore();

      this.cpuEngine = new HardwareTierEngine(DEFAULT_CPU_TIERS);
      this.gpuEngine = new HardwareTierEngine(DEFAULT_GPU_TIERS);
      // the tier lists and the live catalog (the parts we sell): the product page waits for them, so it never answers from the fallback copy
      this.dataReady = Promise.all([this.loadCpuTiers(), this.loadGpuTiers(), this.loadCatalog()]).then(() => {
        this.dataIsReady = true;
        if (this.productMode) {
          this.refreshCheck();
          this.followPc();
          this.renderGoBar();
        }
      });
      if (!this.productMode) this.loadCases();
      this.loadPopular().then(() => this.loadTrending());

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onDocClick = this.onDocClick.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      document.addEventListener('click', this.onDocClick);

      if (this.ui.searchInput) {
        this.ui.searchInput.addEventListener('input', () => this.onSearchInput());
        this.ui.searchInput.addEventListener('focus', () => this.onSearchFocus());
        this.ui.searchInput.addEventListener('click', () => this.onSearchFocus());
        this.ui.searchInput.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.ui.searchInput.value) {
            e.preventDefault();
            this.clearSearch();
          }
        });
      }

      this.ui.rails.forEach((container) => {

        container.addEventListener('dragstart', (e) => e.preventDefault());

        // No component-local wheel handler here — the global horizontal-wheel handler in
        // global.js (the exact mechanism the Deals page sliders use, via its
        // `.product-grid.slider` selector match) already covers this rail. A duplicate
        // local copy was re-added at some point; removed again to keep one source of truth.
        // The actual "wheel doesn't work" bug was scroll-snap-type: x mandatory fighting
        // small wheel increments (fixed in CSS), not a missing handler.

        // Click-and-drag with the mouse (native overflow-x only supports wheel/touch/
        // scrollbar). A short drag (under 6px) is still treated as a normal click so
        // selecting a case keeps working.
        let dragActive = false;
        let dragMoved = false;
        let dragStartX = 0;
        let dragStartScroll = 0;
        container.addEventListener('mousedown', (e) => {
          if (e.button !== 0) return;
          dragActive = true;
          dragMoved = false;
          dragStartX = e.clientX;
          dragStartScroll = container.scrollLeft;
          container.classList.add('is-dragging');
        });
        window.addEventListener('mousemove', (e) => {
          if (!dragActive) return;
          const dx = e.clientX - dragStartX;
          if (Math.abs(dx) > 6) dragMoved = true;
          if (dragMoved) container.scrollLeft = dragStartScroll - dx;
        });
        window.addEventListener('mouseup', () => {
          if (!dragActive) return;
          dragActive = false;
          container.classList.remove('is-dragging');
        });
        container.addEventListener(
          'click',
          (e) => {
            if (dragMoved) {
              e.preventDefault();
              e.stopPropagation();
              dragMoved = false;
            }
          },
          { capture: true }
        );
      });

      if (this.ui.dialog) {
        this.ui.dialog.addEventListener('click', (e) => {
          if (e.target === this.ui.dialog) this.ui.dialog.close();
        });
        this.ui.dialog.addEventListener('close', () => {
          document.documentElement.classList.remove('gbgb-scroll-locked');
          document.body.classList.remove('gbgb-scroll-locked');
          this.dialogSlug = null;
          if (this.ui.dialogMedia) {
            const vid = this.ui.dialogMedia.querySelector('video');
            if (vid) {
              try {
                if (vid._hlsInstance) vid._hlsInstance.destroy();
                vid.pause();
                vid.removeAttribute('src');
                vid.load();
              } catch (ve) { /* ignore */ }
            }
            this.ui.dialogMedia.textContent = '';
          }
          if (this.dialogReturn && document.contains(this.dialogReturn)) this.dialogReturn.focus({ preventScroll: true });
        });
      }

      if (this.ui.recDialog) {
        this.ui.recDialog.addEventListener('click', (e) => {
          if (e.target === this.ui.recDialog) this.ui.recDialog.close();
        });
        this.ui.recDialog.addEventListener('close', () => {
          document.documentElement.classList.remove('gbgb-scroll-locked');
          document.body.classList.remove('gbgb-scroll-locked');
        });
      }

      this.onBlockSelect = (e) => {
        if (e.target && this.contains(e.target)) {
          e.target.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
        }
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.whenNear(() => {
        this.fillMissingFeaturedArt();
      });

      // Captured here, once, from the untouched markup. Capturing lazily at click time
      // saved the already-swapped loading label as the "original", so the reset below
      // put the loading text right back and the button stayed stuck.
      this._origCtaText = this.ui.go ? this.ui.go.textContent : '';
      this._origRecHtml = this.ui.recProceed ? this.ui.recProceed.innerHTML : '';

      // pageshow fires on every view, including back/forward-cache restores, where the
      // page comes back frozen exactly as it was left: mid-redirect, still loading.
      this.onPageShow = () => {
        this.resetLoadingState();
      };
      if (!this.productMode) window.addEventListener('pageshow', this.onPageShow);

      this.onWindowResize = () => this.positionResultsDropdown();
      window.addEventListener('resize', this.onWindowResize);

      // the configurator above is ready (gbg:configurator) or the PC changed (gbg:config): the brand tabs follow it and the
      // recommendation is checked against this product again
      this.onConfigurator = () => { this.followPc(); this.renderGoBar(); };
      if (this.productMode) {
        document.addEventListener('gbg:configurator', this.onConfigurator);
        document.addEventListener('gbg:config', this.onConfigurator);
      }

      this.sync(false);
      // something typed before this script finished loading (the product page loads it on first use)
      if (this.productMode && this.ui.searchInput && this.ui.searchInput.value.trim().length >= 2) this.onSearchInput();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('gbg:configurator', this.onConfigurator);
      document.removeEventListener('gbg:config', this.onConfigurator);
      if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
      if (this.onWindowResize) window.removeEventListener('resize', this.onWindowResize);
      if (this.io) this.io.disconnect();
      if (this.searchAbort) this.searchAbort.abort();
      this.ready = false;
    }

    onDocClick(e) {
      if (this.ui.results && !this.ui.results.hidden) {
        const searchContainer = this.querySelector('.gbgb-search-container') || this.querySelector('.gbgb-search');
        if (searchContainer && !searchContainer.contains(e.target)) {
          this.exitSearch();
        }
      }
    }

    whenNear(fn) {
      if (!('IntersectionObserver' in window)) {
        fn();
        return;
      }
      this.io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.io.disconnect();
          fn();
        }
      }, { rootMargin: '600px 0px' });
      this.io.observe(this);
    }

    /* ---------- Game registry ---------- */

    readFeatured(li) {
      const d = li.dataset;
      const img = li.querySelector('.gbgb-game__art img');
      const min = parseManual(d.min);
      const rec = parseManual(d.rec);
      const manual = min.length || rec.length;
      return {
        appid: d.appid || d.slug || '',
        slug: d.appid || d.slug || '',
        name: d.name,
        source: 'featured',
        image: img ? img.getAttribute('src') : '',
        fps: d.fps || '',
        fpsFilter: d.fpsFilter || '',
        link: d.link || '',
        linkLabel: d.linkLabel || '',
        requirements: manual ? { minimum: min, recommended: rec, source: d.specsSource || 'the publisher', manual: true } : null,
      };
    }

    register(g) {
      if (!g || !g.appid) return null;
      const existing = this.registry.get(g.appid);
      if (!existing) {
        this.registry.set(g.appid, g);
        return g;
      }
      // Keep what we already know (pinned data wins), fill the gaps.
      Object.keys(g).forEach((k) => {
        if ((existing[k] === undefined || existing[k] === null || existing[k] === '') && g[k] != null && g[k] !== '') {
          existing[k] = g[k];
        }
      });
      return existing;
    }

    /* ---------- Steam requests ---------- */

    async ensureDetails(g) {
      if (!g || !g.appid) return g;
      if (g.requirements && g.detailsLoaded && g.trailerLoaded) return g;
      if (!/^\d+$/.test(g.appid)) {
        g.detailsLoaded = true;
        g.trailerLoaded = true;
        return g;
      }
      const ck = `steam:game:${g.appid}`;
      let info = cache.get(ck);
      let failed = false;
      if (!info) {
        try {
          let res = await fetch(`${this.steamProxy}/appdetails?appids=${g.appid}`);
          if (!res.ok) {
            // One retry — the proxy/Steam intermittently rate-limits the first hit.
            await new Promise((r) => setTimeout(r, 600));
            res = await fetch(`${this.steamProxy}/appdetails?appids=${g.appid}`);
          }
          if (!res.ok) throw new Error(`Steam proxy ${res.status}`);
          const json = await res.json();
          const data = json && json[g.appid] && json[g.appid].data;
          let reqs = null;
          if (data && typeof data.pc_requirements === 'object' && !Array.isArray(data.pc_requirements)) {
            const min = parseSpecs(data.pc_requirements.minimum || '');
            const rec = parseSpecs(data.pc_requirements.recommended || '');
            if (min.length || rec.length) {
              reqs = { minimum: min, recommended: rec, source: 'Steam' };
            }
          }
          let trailer = null;
          let trailerHls = null;
          let trailerPoster = '';
          if (data && Array.isArray(data.movies) && data.movies.length > 0) {
            const m = data.movies[0];
            trailer = (m.mp4 && (m.mp4.max || m.mp4['480'])) ||
                      (m.webm && (m.webm.max || m.webm['480'])) ||
                      null;
            if (!trailer && m.hls_h264) trailerHls = m.hls_h264;
            trailerPoster = m.thumbnail || '';
          }
          info = {
            requirements: reqs,
            trailer,
            trailerHls,
            trailerPoster,
            name: data && data.name ? data.name : g.name,
            released: data && data.release_date ? data.release_date.date : ''
          };
        } catch (e) {
          info = { requirements: null, trailer: null, trailerPoster: '' };
          failed = true;
        }
        // A failed lookup must never be cached as "this game has no requirements" — that
        // pins the baseline GTX 1660 SUPER fallback until the cache expires.
        if (!failed) cache.set(ck, info, TTL.game);
      }
      g.requirements = g.requirements || info.requirements;
      g.trailer = g.trailer || info.trailer || null;
      g.trailerHls = g.trailerHls || info.trailerHls || null;
      g.trailerPoster = g.trailerPoster || info.trailerPoster || '';
      if (info.released && !g.released) g.released = info.released;
      // Update image to Steam CDN if we only had a Shopify shop_image
      if (!g.image || g.image.includes('shop_images')) {
        g.image = steamImg(g.appid, 'portrait');
      }
      g.detailsFailed = failed;
      g.detailsLoaded = true;
      g.trailerLoaded = true;
      return g;
    }

    fillMissingFeaturedArt() {
      this.querySelectorAll('[data-gbgb-game][data-source="featured"]').forEach((li) => {
        const art = li.querySelector('.gbgb-game__art');
        if (!art || art.querySelector('img')) return;
        const g = this.registry.get(li.dataset.appid || li.dataset.slug);
        if (g && g.appid) {
          const url = g.image || steamImg(g.appid, 'portrait');
          art.appendChild(makeImg(url, 420));
        }
      });
    }

    /* ---------- Popular Games ---------- */

    async loadPopular() {
      const { popular, popularGrid, morePopular } = this.ui;
      if (!popularGrid) return;

      // Register any server-rendered pinned games first
      const featuredItems = this.querySelectorAll('[data-gbgb-game][data-source="featured"]');
      if (featuredItems.length > 0) {
        this.popularList = [];
        featuredItems.forEach((li) => {
          const g = this.readFeatured(li);
          if (g) {
            this.register(g);
            this.popularList.push(g);
          }
        });
        this.fillMissingFeaturedArt();
        this.syncCards();
        return;
      }

      // If no server-rendered pinned games exist, fallback to SteamSpy
      const ck = 'steam:popular:v1';
      this.renderSkeletons(popularGrid, this.getBatchSize());

      try {
        let list = cache.get(ck);
        if (!list) {
          const res = await fetch(`${this.steamProxy}/trending`);
          if (!res.ok) throw new Error(`SteamSpy proxy ${res.status}`);
          const json = await res.json();
          list = Object.values(json)
            .filter((g) => !ADULT_RE.test(g.name || ''))
            .sort((a, b) => (b.ccu || 0) - (a.ccu || 0))
            .slice(0, 50)
            .map((g) => ({
              appid: String(g.appid),
              name: g.name,
              image: steamImg(g.appid, 'portrait'),
              source: 'popular',
            }));
          cache.set(ck, list, TTL.trending);
        }
        this.popularList = (list || []).map((g) => this.register(g));
        popularGrid.textContent = '';
        this.popularShown = 0;
        this.popularClicks = 0;
        if (!this.popularList.length) {
          if (popular) popular.hidden = true;
          return;
        }
        this.showMorePopular(false);
      } catch (e) {
        popularGrid.textContent = '';
        const p = document.createElement('p');
        p.className = 'gbgb-note';
        p.textContent = 'Popular games could not be loaded right now. Try searching for your game above.';
        popularGrid.appendChild(p);
      }
    }

    showMorePopular(isUserClick = false) {
      if (isUserClick) {
        this.popularClicks += 1;
      }
      const batch = this.getBatchSize();
      const next = this.popularList.slice(this.popularShown, this.popularShown + batch);
      next.forEach((g) => this.ui.popularGrid.appendChild(this.makeCard(g)));
      this.popularShown += next.length;
      if (this.ui.morePopular) {
        const canShowMore = !this.productMode && this.popularClicks < 2 && this.popularShown < this.popularList.length;
        this.ui.morePopular.hidden = !canShowMore;
      }
      this.syncCards();
    }

    /* ---------- New & Trending Games (from SteamSpy) ---------- */

    async loadTrending() {
      const { trending, trendingGrid, moreTrending } = this.ui;
      if (!trendingGrid) return;
      const ck = 'steam:trending:v1';

      this.renderSkeletons(trendingGrid, this.getBatchSize());

      try {
        let list = cache.get(ck);
        if (!list) {
          const res = await fetch(`${this.steamProxy}/trending`);
          if (!res.ok) throw new Error(`SteamSpy proxy ${res.status}`);
          const json = await res.json();
          list = Object.values(json)
            .filter((g) => !ADULT_RE.test(g.name || ''))
            .sort((a, b) => (b.ccu || 0) - (a.ccu || 0))
            .map((g) => ({
              appid: String(g.appid),
              name: g.name,
              image: steamImg(g.appid, 'portrait'),
              source: 'trending',
            }));
          cache.set(ck, list, TTL.trending);
        }
        const knownAppids = new Set([...this.registry.keys()]);
        const filtered = (list || []).filter((g) => !knownAppids.has(g.appid));
        this.trendingList = filtered.map((g) => this.register(g));
        trendingGrid.textContent = '';
        this.trendingShown = 0;
        this.trendingClicks = 0;
        if (!this.trendingList.length) {
          if (trending) trending.hidden = true;
          return;
        }
        this.showMoreTrending(false);
      } catch (e) {
        if (trending) trending.hidden = true;
      }
    }

    showMoreTrending(isUserClick = false) {
      if (isUserClick) {
        this.trendingClicks += 1;
      }
      const batch = this.getBatchSize();
      const next = this.trendingList.slice(this.trendingShown, this.trendingShown + batch);
      next.forEach((g) => this.ui.trendingGrid.appendChild(this.makeCard(g)));
      this.trendingShown += next.length;
      if (this.ui.moreTrending) {
        const canShowMore = this.trendingClicks < 2 && this.trendingShown < this.trendingList.length;
        this.ui.moreTrending.hidden = !canShowMore;
      }
      this.syncCards();
    }

    /* ---------- Search ---------- */

    // A static CSS max-height (e.g. "90vh") can't know the dropdown's actual position —
    // it varies with scroll and viewport size — so it either clips too early or still
    // overflows past the bottom of the screen. This measures the real remaining space
    // below the dropdown's own top edge and caps it to exactly that, every time it opens.
    positionResultsDropdown() {
      const { results, searchInput } = this.ui;
      if (!results || results.hidden) return;
      const margin = 16;
      const minRoom = 260; // enough to comfortably show one full row of 6 tiles
      const rect = results.getBoundingClientRect();
      const available = window.innerHeight - rect.top - margin;
      // If the search box itself is already near/past the bottom of the viewport (e.g.
      // after scrolling down to it), no amount of shrinking the dropdown helps — it would
      // still start off-screen. Scroll the box toward the top of the viewport first so
      // there's actually room below it.
      if (available < minRoom && searchInput) {
        searchInput.scrollIntoView({ block: 'start' });
      }
      this.fitDropdownTiles();
    }

    // No scrollbar, ever, by contract — this measures the real space below the dropdown
    // and shrinks the tile art (never grows it past natural size) so every row of
    // whatever's rendered actually fits. Rerun any time the result count or column count
    // could have changed: after rendering results, and on window resize.
    // Tile size in the dropdown is fixed (see .gbgb-results-dropdown .gbgb-game__art in
    // CSS) — it never changes between searches. Instead, this works out how many results
    // actually fit in whatever space is available below the search box and trims the
    // grid down to that count. Fewer results shown is the correct outcome, not a
    // scrollbar and not a size that shifts around per search.
    computeMaxVisibleResults(cols) {
      const { results, resultsGrid } = this.ui;
      if (!results) return cols;
      const rect = results.getBoundingClientRect();
      const bottomMargin = 16;
      const dropdownVerticalPadding = 32; // .gbgb-results-dropdown padding: 16px top+bottom
      const rowGap = 16;
      // Tile art keeps the correct 2:3 aspect ratio (see .gbgb-game__art in CSS) instead
      // of a fixed height, so its real height depends on how wide a column actually
      // renders at the current column count. Measure it rather than assume it.
      let artHeight = DROPDOWN_TILE_ART_HEIGHT;
      if (resultsGrid && resultsGrid.clientWidth && cols > 0) {
        const colGap = parseFloat(getComputedStyle(resultsGrid).columnGap) || 0;
        const colWidth = (resultsGrid.clientWidth - colGap * (cols - 1)) / cols;
        if (colWidth > 0) artHeight = colWidth * 1.5; // width:height of 2:3 → height = width * 3/2
      }
      const rowHeight = artHeight + DROPDOWN_TILE_TEXT_HEIGHT;
      const available = window.innerHeight - rect.top - bottomMargin - dropdownVerticalPadding;
      const maxRows = Math.max(1, Math.floor((available + rowGap) / (rowHeight + rowGap)));
      return Math.max(cols, maxRows * cols);
    }

    fitDropdownTiles() {
      const { results, resultsGrid } = this.ui;
      if (!results || results.hidden || !resultsGrid) return;
      const items = resultsGrid.children.length;
      if (!items) return;
      const colsRaw = getComputedStyle(resultsGrid).gridTemplateColumns.split(' ').length;
      const cols = colsRaw > 0 ? colsRaw : 1;
      const maxVisible = this.computeMaxVisibleResults(cols);
      // Defensive trim only — search() already caps the fetch to this count. If the
      // column count changed (e.g. window resized) since render, drop the overflow.
      while (resultsGrid.children.length > maxVisible) {
        resultsGrid.removeChild(resultsGrid.lastElementChild);
      }
    }

    onSearchFocus() {
      const qv = (this.ui.searchInput.value || '').trim();
      if (qv.length >= 2) {
        if (this.ui.results) {
          this.ui.results.hidden = false;
          this.positionResultsDropdown();
          if (this.ui.resultsGrid && this.ui.resultsGrid.children.length === 0) {
            this.search(qv);
          }
        }
      }
    }

    onSearchInput() {
      const value = this.ui.searchInput.value;
      const qv = value.trim();
      this.ui.searchClear.hidden = !value;
      clearTimeout(this.searchTimer);
      if (qv.length < 2) {
        this.exitSearch();
        return;
      }
      this.searchTimer = setTimeout(() => this.search(qv), 350);
    }

    clearSearch() {
      this.ui.searchInput.value = '';
      this.ui.searchClear.hidden = true;
      clearTimeout(this.searchTimer);
      this.exitSearch();
      this.ui.searchInput.focus();
    }

    exitSearch() {
      this.searchSeq += 1;
      if (this.ui.results) this.ui.results.hidden = true;
    }

    async search(qv) {
      const seq = ++this.searchSeq;
      const { results, resultsNote, resultsGrid } = this.ui;
      if (!results) return;
      results.hidden = false;
      this.positionResultsDropdown();
      if (resultsNote) resultsNote.hidden = true;
      this.renderSkeletons(resultsGrid, 6);

      try {
        const ck = `steam:search:${qv.toLowerCase()}`;
        let rawItems = cache.get(ck);
        if (!rawItems) {
          const res = await fetch(`${this.steamProxy}/search?term=${encodeURIComponent(qv)}`);
          if (!res.ok) throw new Error(`Steam search proxy ${res.status}`);
          const json = await res.json();
          rawItems = (json && Array.isArray(json.items)) ? json.items : [];
          cache.set(ck, rawItems, TTL.search);
        }
        if (seq !== this.searchSeq) return;
        const colsRaw = getComputedStyle(resultsGrid).gridTemplateColumns.split(' ').length;
        const cols = colsRaw > 0 ? colsRaw : 1;
        const maxVisible = Math.min(12, this.computeMaxVisibleResults(cols));
        const matches = rawItems
          .filter((it) => it && it.type === 'app' && it.name && !JUNK_RE.test(it.name) && !ADULT_RE.test(it.name))
          .slice(0, maxVisible);
        const list = matches.map((it) => ({
          appid: String(it.id),
          name: it.name,
          image: steamImg(it.id, 'portrait'),
          fallbackImage: it.tiny_image || '',
          source: 'search',
        }));
        resultsGrid.textContent = '';
        if (!list.length) {
          if (resultsNote) {
            resultsNote.textContent = `No PC games match \u201c${qv}\u201d. Check the spelling or try a shorter name.`;
            resultsNote.hidden = false;
          }
          return;
        }
        list.forEach((r) => resultsGrid.appendChild(this.makeCard(this.register(r))));
        this.syncCards();
        this.fitDropdownTiles();
        this.announce(`${list.length} games found.`);
      } catch (e) {
        if (seq !== this.searchSeq) return;
        resultsGrid.textContent = '';
        if (resultsNote) {
          resultsNote.textContent = 'Search isn\u2019t available right now. Pick from the games above, or try again in a minute.';
          resultsNote.hidden = false;
        }
      }
    }

    /* ---------- Rendering ---------- */

    renderSkeletons(grid, n) {
      grid.textContent = '';
      for (let i = 0; i < n; i += 1) {
        const li = document.createElement('li');
        li.className = 'gbgb-game is-skeleton';
        li.setAttribute('aria-hidden', 'true');
        li.innerHTML = '<span class="gbgb-game__art"></span><span class="gbgb-game__name"></span>';
        grid.appendChild(li);
      }
    }

    makeCard(g, isTray = false) {
      const li = document.createElement('li');
      li.className = 'gbgb-game';
      li.setAttribute('data-gbgb-game', '');
      li.dataset.appid = g.appid;
      li.dataset.slug = g.appid;
      li.dataset.source = g.source || 'steam';

      if (isTray) {
        li.classList.add('is-selected');
        li.setAttribute('data-gbgb-tray-card', '');
      }

      const pick = document.createElement(isTray ? 'div' : 'button');
      if (!isTray) {
        pick.type = 'button';
        pick.setAttribute('aria-pressed', 'false');
        pick.setAttribute('data-gbgb-toggle', '');
      }
      pick.className = 'gbgb-game__pick';

      const art = document.createElement('span');
      art.className = 'gbgb-game__art';
      if (g.image) art.appendChild(makeImg(g.image, 420, '', g.fallbackImage));

      const name = document.createElement('span');
      name.className = 'gbgb-game__name';
      name.textContent = g.name;

      pick.append(art, name);

      const meta = document.createElement('div');
      meta.className = 'gbgb-game__meta';
      if (g.fps) {
        const fps = document.createElement('span');
        fps.className = 'gbgb-game__fps';
        fps.textContent = g.fps;
        meta.appendChild(fps);
      }
      const specs = document.createElement('button');
      specs.type = 'button';
      specs.className = 'gbgb-game__specs';
      specs.setAttribute('data-gbgb-specs', '');
      specs.textContent = 'Specs';
      const vh = document.createElement('span');
      vh.className = 'gbgb-vh';
      vh.textContent = ` for ${g.name}`;
      specs.appendChild(vh);
      meta.appendChild(specs);

      li.append(pick, meta);

      if (isTray) {
        const rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'gbgb-game__remove';
        rm.dataset.gbgbRemove = g.appid;
        rm.setAttribute('aria-label', `Remove ${g.name}`);
        rm.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M5 5l14 14M19 5L5 19"/></svg>';
        li.appendChild(rm);
      }

      return li;
    }

    syncCards() {
      this.querySelectorAll('[data-gbgb-game]:not([data-gbgb-tray-card])').forEach((li) => {
        const on = this.selected.has(li.dataset.appid || li.dataset.slug);
        li.classList.toggle('is-selected', on);
        const btn = li.querySelector('[data-gbgb-toggle]');
        if (btn) btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    renderTray() {
      const { chips, count, empty } = this.ui;
      if (!chips) return;
      chips.textContent = '';
      this.selected.forEach((g) => {
        chips.appendChild(this.makeCard(g, true));
      });
      const n = this.selected.size;
      if (count) count.textContent = n ? `(${n})` : '';
      if (empty) empty.hidden = n > 0;
      this.syncCards();
    }

    // Case clearance data (same gbg-cases.json the configurator uses). Returns
    // undefined while still loading, null when no case is picked / not in the book.
    caseRecord() {
      if (!this.caseSel) return null;
      if (!this.caseBook) return undefined;
      return window.GBGCompat.findCase(this.caseBook, [this.caseSel.fullTitle, this.caseSel.title, this.caseSel.handle]);
    }

    gpuPart(id) {
      const list = (this.catalogData && this.catalogData.parts && this.catalogData.parts.gpu) || [];
      return list.find((x) => x.id === id) || null;
    }

    // Single source of truth for the recommended CPU + GPU. Returns null until every
    // input is real (game requirements, catalog, case clearances all loaded).
    // `blocked` is set when the compatibility rules say the recommended parts cannot be
    // had in the case - on the by-game page the shared rules (gbg-compat.js) against the
    // chosen case, on a product page the configurator's engine (the page's own case) - and
    // the build is never sent / the parts are never given to the configurator.
    resolveRecommendation(games, tierMode) {
      if (!window.GBGCompat) return null;
      if (!this.cpuEngine || !this.gpuEngine) return null;
      if (games.some((g) => !g.requirements && !g.detailsLoaded && /^\d+$/.test(g.appid))) return null;
      if (this.productMode && !(this.dataIsReady && window.GBG_configurator)) return null;
      const cRec = this.productMode ? null : this.caseRecord();
      if (cRec === undefined || (cRec && !this.catalogData)) return null;

      const cpuEval = this.cpuEngine.evaluate(games, tierMode);
      const gpuEval = this.gpuEngine.evaluate(games, tierMode);
      const cr = (cpuEval && cpuEval.recommendations) || {};
      const gr = (gpuEval && gpuEval.recommendations) || {};
      const cpu = (this.selectedCpuBrand === 'intel' ? cr.intel : cr.amd) || cr.primary;
      const gpu = (this.selectedGpuBrand === 'amd' ? gr.amd : gr.nvidia) || gr.primary;
      if (!cpu || !gpu) return null;
      const ramGb = this.ramChosen || recommendedRamGb(games, tierMode);

      let blocked = null;
      if (this.productMode) {
        const problem = window.GBG_configurator.check({ cpu: cpu.id, gpu: gpu.id, ramGb });
        // a card that is too long for this case is cured by another case; any other reason is not something the shopper can fix
        if (problem) blocked = { message: problem.message, label: problem.code === 'gpu-too-long' ? null : 'Not Available For This PC' };
      } else if (cRec) {
        const check = window.GBGCompat.checkBuild({
          caseRec: cRec,
          rules: this.caseBook && this.caseBook.rules,
          gpu: this.gpuPart(gpu.id),
        });
        if (!check.ok) blocked = { issues: check.issues, message: check.issues.map((i) => i.message).join(' ') };
      }
      return { cpu, gpu, ramGb, cpuEval, gpuEval, tierMode, blocked };
    }

    // Grey out the main CTA when the recommended build cannot fit the chosen case.
    applyGoState() {
      const btn = this.ui && this.ui.go;
      if (!btn || btn.getAttribute('aria-busy') === 'true') return;
      const blocked = this.shownRec && this.shownRec.blocked;
      btn.classList.toggle('is-blocked', !!blocked);
      btn.disabled = !!blocked;
      if (blocked) {
        btn.setAttribute('aria-disabled', 'true');
        btn.textContent = blocked.label || 'Choose a Larger Case';
      } else {
        btn.removeAttribute('aria-disabled');
        if (this._origCtaText) btn.textContent = this._origCtaText;
      }
    }

    renderGoBar() {
      const { summary } = this.ui;
      const games = [...this.selected.values()];
      const tierMode = this.tierSelection || 'recommended';

      // The recommendation is resolved ONCE here and stored as this.shownRec. go() hands
      // exactly this object to the customizer - it never recomputes and never substitutes
      // a default part, so what the customer sees is always what gets built.
      this.shownRec = null;
      const rec = games.length ? this.resolveRecommendation(games, tierMode) : null;

      if (games.length && !rec) {
        // Requirements / catalog / case data still loading: show no build at all rather
        // than a guessed one that could change a moment later.
        summary.innerHTML = '<span style="color: var(--gbgb-green, #1eff2c); font-weight: 700;">Calculating your recommendation…</span>';
      } else if (rec) {
        this.shownRec = rec;
        const cpuName = rec.cpu.shortName || rec.cpu.name;
        const gpuName = rec.gpu.shortName || rec.gpu.name;
        const note = rec.blocked
          ? ` <span style="display: block; font-weight: 400; color: #ff8a80; font-size: 0.85em;">${rec.blocked.message}</span>`
          : '';
        summary.innerHTML = `<span style="color: var(--gbgb-green, #1eff2c); font-weight: 700;">We Recommend:</span> <span style="font-weight: 600; color: #fff;">${cpuName} + ${gpuName} + ${rec.ramGb}GB RAM</span>${note}`;
      } else if (this.caseSel) {
        summary.textContent = `${this.caseSel.title}. Now pick your games.`;
      } else {
        summary.textContent = summary.dataset.empty || 'Pick your games to see our recommendation.';
      }

      this.applyRamTabs(this.ramChosen || recommendedRamGb(games, tierMode));
      this.applyGoState();
      this.renderTierDebug(games, tierMode);
    }

    applyRamTabs(gb) {
      (this.ui.ramTabs || []).forEach((btn) => {
        const isMatch = Number(btn.dataset.gbgbRamBtn) === gb;
        btn.classList.toggle('is-active', isMatch);
        btn.setAttribute('aria-selected', isMatch ? 'true' : 'false');
      });
    }

    // Verification panel: shows the raw peak game requirement per brand (not a catalog
    // part — the actual "what does the toughest picked game ask for" number) for both
    // minimum and recommended, then the real catalog part each tier resolves to.
    renderTierDebug(games, tierMode) {
      const { debug, debugMin, debugRec, debugFinal } = this.ui;
      if (!debug) return;

      if (!games.length) {
        debug.hidden = true;
        return;
      }
      debug.hidden = false;

      // Shows the literal requirement text as the game/Steam published it — not our
      // tier-matched model name. primary/secondary can be driven by different selected
      // games, so both are shown when they differ; collapsed to one when it's the same game.
      const fmtPeak = (peak) => {
        if (!peak) return '—';
        const entries = [];
        if (peak.primary) entries.push({ game: peak.primary.gameName || '?', raw: peak.primary.rawSpec || '—' });
        if (peak.secondary && peak.secondary.gameName !== (peak.primary && peak.primary.gameName)) {
          entries.push({ game: peak.secondary.gameName || '?', raw: peak.secondary.rawSpec || '—' });
        }
        return entries.map((e) => `"${e.raw}" (${e.game})`).join(' · ') || '—';
      };

      const cpuMin = this.cpuEngine ? this.cpuEngine.findPeak(games, 'minimum') : null;
      const cpuRec = this.cpuEngine ? this.cpuEngine.findPeak(games, 'recommended') : null;
      const gpuMin = this.gpuEngine ? this.gpuEngine.findPeak(games, 'minimum') : null;
      const gpuRec = this.gpuEngine ? this.gpuEngine.findPeak(games, 'recommended') : null;

      if (debugMin) debugMin.textContent = `CPU — ${fmtPeak(cpuMin)}  |  GPU — ${fmtPeak(gpuMin)}`;
      if (debugRec) debugRec.textContent = `CPU — ${fmtPeak(cpuRec)}  |  GPU — ${fmtPeak(gpuRec)}`;

      const cpuOffer = this.cpuEngine ? this.cpuEngine.getRecommendationsForTier(games, tierMode) : null;
      const gpuOffer = this.gpuEngine ? this.gpuEngine.getRecommendationsForTier(games, tierMode) : null;
      const cpuObj = (this.selectedCpuBrand === 'intel' ? cpuOffer && cpuOffer.intel : cpuOffer && cpuOffer.amd) || (cpuOffer && cpuOffer.primary);
      const gpuObj = (this.selectedGpuBrand === 'amd' ? gpuOffer && gpuOffer.amd : gpuOffer && gpuOffer.nvidia) || (gpuOffer && gpuOffer.primary);
      const cpuPart = cpuObj ? (cpuObj.name || cpuObj.shortName) : '—';
      const gpuPart = gpuObj ? (gpuObj.name || gpuObj.shortName) : '—';
      if (debugFinal) debugFinal.textContent = `Tier: ${tierMode}  |  CPU (${(this.selectedCpuBrand || 'amd').toUpperCase()}) — ${cpuPart}  |  GPU (${(this.selectedGpuBrand || 'nvidia').toUpperCase()}) — ${gpuPart}`;
    }

    sync(persist = true) {
      this.syncCards();
      this.renderTray();
      this.renderGoBar();
      if (this.dialogSlug) this.updateDialogToggle();
      if (!this.ui.error.hidden && this.selected.size && (this.productMode || this.caseSel)) this.ui.error.hidden = true;
      if (persist) this.persist();
    }

    announce(msg) {
      if (!this.ui.live) return;
      this.ui.live.textContent = '';
      requestAnimationFrame(() => { this.ui.live.textContent = msg; });
    }

    /* ---------- Selection ---------- */

    toggle(slug, opts = {}) {
      const appid = slug;
      const g = this.registry.get(appid);
      if (!g) return;
      if (this.selected.has(appid)) {
        this.selected.delete(appid);
        this.announce(`${g.name} removed. ${this.selected.size} ${this.selected.size === 1 ? 'game' : 'games'} picked.`);
      } else {
        this.selected.set(appid, g);
        this.announce(`${g.name} added. ${this.selected.size} ${this.selected.size === 1 ? 'game' : 'games'} picked.`);
        // product page: the pick is confirmed in the page's own message style (owner 2026-10-11)
        if (this.productMode && window.GBG_configurator) window.GBG_configurator.toast('Game Saved!');
        if (opts.fromSearch) this.ensureDetails(g).then(() => this.logSearch(g)).catch(() => {});
        if (!g.requirements) {
          this.ensureDetails(g).then(() => {
            this.renderGoBar();
          }).catch(() => {});
        }
      }
      this.sync();
    }

    setCase(input) {
      if (!input) return;
      this.querySelectorAll('[data-gbgb-case-item]').forEach((item) => {
        const isMatch = item.contains(input);
        item.classList.toggle('is-selected', isMatch);
        const radio = item.querySelector('[data-gbgb-case]');
        if (radio) radio.checked = isMatch;
      });
      this.caseSel = {
        handle: input.value,
        title: input.dataset.title || input.value,
        fullTitle: input.dataset.fullTitle || '',
        url: input.dataset.url || '',
        image: input.dataset.image || '',
        mediaId: input.dataset.mediaId || '',
        price: Number(input.dataset.price) || 0,
      };
      this.announce(`${this.caseSel.title} selected.`);
      this.sync();
    }

    setTier(tier) {
      if (!tier || tier === this.tierSelection) return;
      this.tierSelection = tier;
      this.ui.tierTabs.forEach((btn) => {
        const isMatch = btn.dataset.gbgbTierBtn === tier;
        btn.classList.toggle('is-active', isMatch);
        btn.setAttribute('aria-selected', isMatch ? 'true' : 'false');
      });
      if (this.ui.tierDesc) {
        this.ui.tierDesc.textContent = TIER_COPY[tier] || '';
      }
      this.renderGoBar();
    }

    setCpuBrand(brand, silent = false) {
      if (!brand || brand === this.selectedCpuBrand) return;
      this.selectedCpuBrand = brand;
      this.ui.cpuTabs.forEach((btn) => {
        const isMatch = btn.dataset.gbgbCpuBtn === brand;
        btn.classList.toggle('is-active', isMatch);
        btn.setAttribute('aria-selected', isMatch ? 'true' : 'false');
      });
      if (!silent) this.announce(`CPU brand set to ${brand.toUpperCase()}`);
      this.renderGoBar();
      this.persist();
    }

    setGpuBrand(brand, silent = false) {
      if (!brand || brand === this.selectedGpuBrand) return;
      this.selectedGpuBrand = brand;
      this.ui.gpuTabs.forEach((btn) => {
        const isMatch = btn.dataset.gbgbGpuBtn === brand;
        btn.classList.toggle('is-active', isMatch);
        btn.setAttribute('aria-selected', isMatch ? 'true' : 'false');
      });
      if (!silent) this.announce(`GPU brand set to ${brand.toUpperCase()}`);
      this.renderGoBar();
      this.persist();
    }

    setRam(gb) {
      gb = Number(gb);
      if (!RAM_CHOICES.includes(gb) || gb === this.ramChosen) return;
      this.ramChosen = gb;
      this.announce(`RAM set to ${gb}GB`);
      this.renderGoBar();
      this.persist();
    }

    persist() {
      if (this.productMode) return; // a product page starts fresh, like its configurator
      try {
        const games = [...this.selected.values()].map((g) => ({
          appid: g.appid, slug: g.appid, id: g.id || g.appid || null, name: g.name, image: g.image || '', source: g.source,
          fps: g.fps || '', fpsFilter: g.fpsFilter || '', link: g.link || '', linkLabel: g.linkLabel || '',
          requirements: g.requirements || null, detailsLoaded: Boolean(g.detailsLoaded),
        }));
        sessionStorage.setItem(`gbgb:state:${this.sectionId}`, JSON.stringify({
          games,
          case: this.caseSel && this.caseSel.handle,
          tier: this.tierSelection,
          cpuBrand: this.selectedCpuBrand,
          gpuBrand: this.selectedGpuBrand,
          ramGb: this.ramChosen
        }));
      } catch (e) { /* private mode */ }
    }

    restore() {
      let saved = null;
      try { saved = JSON.parse(sessionStorage.getItem(`gbgb:state:${this.sectionId}`) || 'null'); } catch (e) { saved = null; }
      if (saved) {
        (saved.games || []).forEach((g) => {
          const item = { ...g, appid: g.appid || g.slug };
          const reg = this.register(item);
          if (reg) this.selected.set(reg.appid, reg);
        });
        if (saved.case) {
          const input = [...this.querySelectorAll('[data-gbgb-case]')].find((el) => el.value === saved.case);
          if (input) {
            this.setCase(input);
          }
        }
        if (saved.tier) this.setTier(saved.tier);
        if (saved.cpuBrand) this.setCpuBrand(saved.cpuBrand);
        if (saved.gpuBrand) this.setGpuBrand(saved.gpuBrand);
        if (saved.ramGb) this.setRam(saved.ramGb);
      }
    }

    /* ---------- Events ---------- */

    onClick(event) {
      const t = event.target;
      const tierBtn = t.closest('[data-gbgb-tier-btn]');
      if (tierBtn) {
        this.setTier(tierBtn.dataset.gbgbTierBtn);
        return;
      }
      const cpuBtn = t.closest('[data-gbgb-cpu-btn]');
      if (cpuBtn) {
        this.brandChosen = true;
        this.setCpuBrand(cpuBtn.dataset.gbgbCpuBtn);
        return;
      }
      const gpuBtn = t.closest('[data-gbgb-gpu-btn]');
      if (gpuBtn) {
        this.brandChosen = true;
        this.setGpuBrand(gpuBtn.dataset.gbgbGpuBtn);
        return;
      }
      const ramBtn = t.closest('[data-gbgb-ram-btn]');
      if (ramBtn) {
        this.setRam(ramBtn.dataset.gbgbRamBtn);
        return;
      }
      const toggleBtn = t.closest('[data-gbgb-toggle]');
      if (toggleBtn) {
        if (toggleBtn.closest('[data-gbgb-tray]')) return;
        const li = toggleBtn.closest('[data-gbgb-game]');
        // a game picked from the search results counts as a search (logged for the dashboard)
        if (li) this.toggle(li.dataset.appid || li.dataset.slug, { fromSearch: !!toggleBtn.closest('[data-gbgb-results]') });
        return;
      }
      const specsBtn = t.closest('[data-gbgb-specs]');
      if (specsBtn) {
        const li = specsBtn.closest('[data-gbgb-game]');
        if (li) {
          const sid = li.dataset.appid || li.dataset.slug;
          const fromSearch = !!specsBtn.closest('[data-gbgb-results]');
          this.dialogFromSearch = fromSearch;
          this.openSpecs(sid, specsBtn);
          if (fromSearch) {
            const sg = this.registry.get(sid);
            if (sg) this.ensureDetails(sg).then(() => this.logSearch(sg)).catch(() => {});
          }
        }
        return;
      }
      const rm = t.closest('[data-gbgb-remove]');
      if (rm) {
        const appid = rm.dataset.gbgbRemove;
        const g = this.registry.get(appid);
        this.selected.delete(appid);
        if (g) this.announce(`${g.name} removed.`);
        this.sync();
        const next = this.ui.chips.querySelector('[data-gbgb-remove]');
        (next || this.ui.step1).focus({ preventScroll: true });
        return;
      }
      if (t.closest('[data-gbgb-search-clear]') || t.closest('[data-gbgb-results-close]')) {
        this.clearSearch();
        return;
      }
      const filterColorBtn = t.closest('[data-gbgb-filter-color]');
      if (filterColorBtn) {
        const color = filterColorBtn.dataset.gbgbFilterColor;
        this.activeColorFilter = color;
        this.querySelectorAll('[data-gbgb-filter-color]').forEach(b => b.classList.toggle('is-active', b === filterColorBtn));
        this.applyCaseFilters();
        return;
      }
      const filterCpuBtn = t.closest('[data-gbgb-filter-cpu]');
      if (filterCpuBtn) {
        const cpu = filterCpuBtn.dataset.gbgbFilterCpu;
        this.activeCpuFilter = cpu;
        this.querySelectorAll('[data-gbgb-filter-cpu]').forEach(b => b.classList.toggle('is-active', b === filterCpuBtn));
        this.applyCaseFilters();
        return;
      }
      const moreBtn = t.closest('[data-gbgb-more]') || t.closest('[data-gbgb-more-popular]');
      if (moreBtn) {
        const type = moreBtn.dataset.gbgbMore;
        if (type === 'trending') {
          this.showMoreTrending(true);
        } else {
          this.showMorePopular(true);
        }
        return;
      }
      if (t.closest('[data-gbgb-dialog-fullscreen]')) {
        const wrap = this.ui.dialogMedia;
        const vid = wrap && wrap.querySelector('video');
        const target = vid || wrap;
        if (target) {
          // iOS Safari has no standard Fullscreen API for arbitrary elements at all —
          // <video> there only exposes this separate, non-standard method. Without this
          // branch, requestFullscreen()/webkitRequestFullscreen() are both undefined on
          // iPhone/iPad and the button silently does nothing (works fine on Android/desktop,
          // which do support the standard API).
          if (vid && typeof vid.webkitEnterFullscreen === 'function' && !document.fullscreenElement && !document.webkitFullscreenElement) {
            vid.webkitEnterFullscreen();
          } else if (document.fullscreenElement || document.webkitFullscreenElement) {
            if (document.exitFullscreen) document.exitFullscreen();
            else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
          } else {
            if (target.requestFullscreen) target.requestFullscreen();
            else if (target.webkitRequestFullscreen) target.webkitRequestFullscreen();
            else if (target.msRequestFullscreen) target.msRequestFullscreen();
          }
        }
        return;
      }
      if (t.closest('[data-gbgb-dialog-close]')) {
        this.ui.dialog.close();
        return;
      }
      if (t.closest('[data-gbgb-rec-close]') || t.closest('[data-gbgb-rec-back]')) {
        if (this.ui.recDialog) this.ui.recDialog.close();
        return;
      }
      if (t.closest('[data-gbgb-rec-proceed]')) {
        this.proceedToCustomizer();
        return;
      }
      if (t.closest('[data-gbgb-dialog-toggle]')) {
        if (this.dialogSlug) this.toggle(this.dialogSlug, { fromSearch: !!this.dialogFromSearch });
        return;
      }
      const caseItem = t.closest('[data-gbgb-case-item]');
      if (caseItem) {
        const input = caseItem.querySelector('[data-gbgb-case]');
        if (input) this.setCase(input);
        return;
      }
      if (t.closest('[data-gbgb-go]')) {
        this.go();
      }
    }

    applyCaseFilters() {
      const color = this.activeColorFilter || 'all';
      const cpu = this.activeCpuFilter || 'all';
      this.querySelectorAll('[data-gbgb-case-item]').forEach((item) => {
        const itemColor = (item.dataset.color || '').toLowerCase();
        const itemCpu = (item.dataset.cpu || '').toLowerCase();
        const matchColor = color === 'all' || itemColor === 'multi' || itemColor.includes(color);
        const matchCpu = cpu === 'all' || itemCpu === 'both' || itemCpu.includes(cpu);
        item.hidden = !(matchColor && matchCpu);
      });
    }

    onChange(event) {
      if (event.target.matches('[data-gbgb-case]')) this.setCase(event.target);
    }

    /* ---------- Specs window ---------- */

    async openSpecs(slug, trigger) {
      const g = this.registry.get(slug);
      const { dialog, dialogTitle, dialogYear, dialogLink } = this.ui;
      if (!g || !dialog) return;
      this.dialogSlug = slug;
      this.dialogReturn = trigger || document.activeElement;

      dialogTitle.textContent = g.name;
      const year = extractYear(g.released);
      if (dialogYear) {
        dialogYear.textContent = year || '';
        dialogYear.hidden = !year;
      }
      dialogTitle.classList.toggle('gbgb-dialog__title--wide', !year);

      if (g.link) {
        dialogLink.href = g.link;
        dialogLink.textContent = g.linkLabel || 'See PCs for this game';
        dialogLink.hidden = false;
      } else {
        dialogLink.hidden = true;
      }
      this.updateDialogToggle();
      this.renderDialogMedia(g);
      this.updateDialogSource(g);

      const needsSpecs = !g.requirements && Boolean(g.appid) && /^\d+$/.test(g.appid);
      const needsTrailer = !g.trailerLoaded && Boolean(g.appid) && /^\d+$/.test(g.appid);
      // product-page searchbar: the lines by the columns need the tier lists + catalog (loaded once, a moment after the script)
      const needsData = this.productMode && !this.dataIsReady;
      this.renderSpecs(g, needsSpecs);
      if (!dialog.open) {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        document.documentElement.classList.add('gbgb-scroll-locked');
        document.body.classList.add('gbgb-scroll-locked');
      }
      if (!needsSpecs && !needsTrailer && !needsData) return;

      try {
        await (needsData ? Promise.all([this.ensureDetails(g), this.dataReady]) : this.ensureDetails(g));
      } catch (e) {
        g.fetchFailed = true;
      }
      if (this.dialogSlug !== slug) return;
      const updatedYear = extractYear(g.released);
      if (dialogYear) {
        dialogYear.textContent = updatedYear || '';
        dialogYear.hidden = !updatedYear;
      }
      dialogTitle.classList.toggle('gbgb-dialog__title--wide', !updatedYear);
      this.renderDialogMedia(g);
      if (needsSpecs || needsData) this.renderSpecs(g, false);
      this.updateDialogSource(g);
      g.fetchFailed = false;
    }

    renderDialogMedia(g) {
      const wrap = this.ui.dialogMedia;
      const fsBtn = this.querySelector('[data-gbgb-dialog-fullscreen]');
      const hasVideo = Boolean(g && (g.trailer || g.trailerHls));
      if (fsBtn) {
        fsBtn.style.display = hasVideo ? 'flex' : 'none';
      }
      if (!wrap) return;
      wrap.textContent = '';
      if (hasVideo) {
        const vid = document.createElement('video');
        vid.className = 'gbgb-dialog__trailer';
        vid.poster = g.trailerPoster || g.image || (g.appid ? steamImg(g.appid, 'header') : '');
        vid.autoplay = true;
        vid.muted = true;
        vid.loop = true;
        vid.playsInline = true;
        vid.controls = true;
        vid.setAttribute('aria-label', `${g.name} gameplay trailer`);
        wrap.appendChild(vid);
        if (g.trailer) {
          vid.src = g.trailer;
        } else if (g.trailerHls) {
          attachHlsTrailer(vid, g.trailerHls);
        }
      } else if (g) {
        const img = document.createElement('img');
        img.className = 'gbgb-dialog__banner';
        img.src = (g.appid && /^\d+$/.test(g.appid)) ? steamImg(g.appid, 'header') : (g.image || '');
        img.alt = g.name;
        wrap.appendChild(img);
      }
    }

    updateDialogSource(g) {
      if (!this.ui.dialogSource) return;
      const req = g && g.requirements;
      if (req && req.manual) {
        this.ui.dialogSource.textContent = req.source ? `Specs from ${req.source}.` : 'Specs from the publisher.';
        return;
      }
      if (g && /^\d+$/.test(g.appid)) {
        this.ui.dialogSource.innerHTML = `Specs from the game\u2019s Steam store listing. <a href="https://store.steampowered.com/app/${g.appid}/" target="_blank" rel="noopener" style="color:var(--gbgb-green);text-decoration:underline;">View on Steam</a>`;
      } else {
        this.ui.dialogSource.textContent = 'Specs from the game\u2019s Steam store listing.';
      }
    }

    updateDialogToggle() {
      const btn = this.ui.dialogToggle;
      if (!btn || !this.dialogSlug) return;
      const on = this.selected.has(this.dialogSlug);
      btn.textContent = on ? this.labels.remove : this.labels.add;
      btn.classList.toggle('is-remove', on);
    }

    // Why the popup has no specs table.
    noSpecsText(g) {
      return g.fetchFailed || g.detailsFailed
        ? 'Specs couldn’t load right now. You can still add the game, and we’ll match your build to it.'
        : 'No PC specs are listed for this game yet. You can still add it, and we’ll match your build to it.';
    }

    // Product page: the popup is re-drawn once the tier lists have loaded or the game's details have arrived.
    refreshCheck() {
      const g = this.dialogSlug && this.registry.get(this.dialogSlug);
      if (g && g.requirements && this.ui.dialog && this.ui.dialog.open) this.renderSpecs(g, false);
    }

    // The shopper's current PC: CustomPageCode.js publishes it as window.GBG_check (and fires `gbg:config`) on every change.
    pcNow() {
      const c = window.GBG_check || {};
      return { cpu: String(c.cpu || ''), gpu: String(c.gpu || ''), ramGb: specGb(c.ram) };
    }

    renderSpecs(g, loading) {
      const body = this.ui.dialogBody;
      body.textContent = '';
      if (loading) {
        const wrap = document.createElement('div');
        wrap.className = 'gbgb-loading-state';
        wrap.setAttribute('role', 'status');
        wrap.setAttribute('aria-label', 'Loading system requirements');
        wrap.innerHTML = `
          <svg class="gbgb-spinner" viewBox="0 0 120 120" aria-hidden="true" focusable="false">
            <circle class="gbgb-spinner__track" cx="60" cy="60" r="52"/>
            <circle class="gbgb-spinner__arc" cx="60" cy="60" r="52" pathLength="100"/>
          </svg>
          <p class="gbgb-loading-text">Loading system requirements...</p>
        `;
        body.appendChild(wrap);
        return;
      }
      const req = g.requirements;
      if (!req || (!req.minimum.length && !req.recommended.length)) {
        const p = document.createElement('p');
        p.className = 'gbgb-specs__none';
        p.textContent = this.noSpecsText(g);
        body.appendChild(p);
        return;
      }

      const ICONS = {
        cpu: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round"><rect x="16" y="16" width="32" height="32" rx="3"/><rect x="22" y="22" width="20" height="20" rx="1.5"/><path d="M20 10v6M28 10v6M36 10v6M44 10v6M20 48v6M28 48v6M36 48v6M44 48v6M10 20h6M10 28h6M10 36h6M10 44h6M48 20h6M48 28h6M48 36h6M48 44h6"/></svg>',
        ram: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round"><rect x="6" y="10" width="52" height="38" rx="3"/><rect x="12" y="18" width="12" height="22" rx="1.5"/><rect x="26" y="18" width="12" height="22" rx="1.5"/><rect x="40" y="18" width="12" height="22" rx="1.5"/><path d="M10 48v6M16 48v6M22 48v6M28 48v6M34 48v6M40 48v6M46 48v6M52 48v6"/></svg>',
        gpu: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round"><rect x="6" y="20" width="52" height="24" rx="3"/><circle cx="18" cy="32" r="6"/><circle cx="32" cy="32" r="6"/><circle cx="46" cy="32" r="6"/><circle cx="18" cy="32" r="1.6" fill="currentColor"/><circle cx="32" cy="32" r="1.6" fill="currentColor"/><circle cx="46" cy="32" r="1.6" fill="currentColor"/><path d="M12 44v5M16 44v5M20 44v5M24 44v5M28 44v5"/></svg>',
        storage: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round"><rect x="12" y="21" width="48" height="22" rx="3"/><rect x="17" y="26" width="10" height="12" rx="1.5"/><rect x="31" y="26" width="10" height="12" rx="1.5"/><rect x="45" y="26" width="10" height="12" rx="1.5"/><path d="M4 22h8M4 32h8M4 37h8M4 42h8"/></svg>',
        vram: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round"><rect x="6" y="20" width="52" height="24" rx="3"/><circle cx="18" cy="32" r="6"/><circle cx="32" cy="32" r="6"/><circle cx="46" cy="32" r="6"/><circle cx="18" cy="32" r="1.6" fill="currentColor"/><circle cx="32" cy="32" r="1.6" fill="currentColor"/><circle cx="46" cy="32" r="1.6" fill="currentColor"/><path d="M12 44v5M16 44v5M20 44v5M24 44v5M28 44v5"/></svg>',
        other: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round"><rect x="16" y="16" width="32" height="32" rx="3"/><circle cx="32" cy="32" r="6"/></svg>'
      };

      // Suppress non-hardware rows: network, sound, notes, vr, display, directx, os, other
      const HIDDEN_KEYS = new Set(['os', 'directx', 'network', 'sound', 'notes', 'vr', 'display', 'other']);
      const HIDDEN_VALUES = /requires a 64-bit processor|broadband internet|sound card|directx/i;
      const filterRows = (rs) => (rs || []).filter((r) =>
        !HIDDEN_KEYS.has(r.key) &&
        !HIDDEN_VALUES.test(r.value)
      );

      const minRows = filterRows(req.minimum);
      const recRows = filterRows(req.recommended);

      const minByKey = new Map();
      minRows.forEach((r) => { if (!minByKey.has(r.key)) minByKey.set(r.key, r); });
      const recByKey = new Map();
      recRows.forEach((r) => { if (!recByKey.has(r.key)) recByKey.set(r.key, r); });

      const ORDER = ['cpu', 'gpu', 'ram', 'storage', 'vram'];
      const allKeys = [];
      ORDER.forEach((k) => {
        if (minByKey.has(k) || recByKey.has(k)) allKeys.push(k);
      });
      [...minByKey.keys(), ...recByKey.keys()].forEach((k) => {
        if (!allKeys.includes(k) && !HIDDEN_KEYS.has(k)) allKeys.push(k);
      });

      if (!allKeys.length) {
        const p = document.createElement('p');
        p.className = 'gbgb-specs__none';
        p.textContent = this.noSpecsText(g);
        body.appendChild(p);
        return;
      }

      const hasMin = minByKey.size > 0;
      const hasRec = recByKey.size > 0;
      const isSingle = !hasRec || !hasMin;

      const table = document.createElement('div');
      table.className = `gbgb-specs-table ${isSingle ? 'gbgb-specs-table--single' : ''}`;

      const thead = document.createElement('div');
      thead.className = 'gbgb-specs-table__head';
      thead.innerHTML = `
        <div class="gbgb-specs-table__th gbgb-specs-table__th--cat"><span>&nbsp;</span></div>
        ${hasMin ? '<div class="gbgb-specs-table__th gbgb-specs-table__th--min">MINIMUM</div>' : ''}
        ${hasRec ? '<div class="gbgb-specs-table__th gbgb-specs-table__th--rec">RECOMMENDED</div>' : ''}
      `;
      // product-page searchbar (owner 2026-10-04): how the shopper's PC compares, written by each column
      if (this.productMode) {
        const res = this.dataIsReady ? checkRequirements(g, this.pcNow(), this.cpuEngine, this.gpuEngine) : null;
        [['min', 'minimum'], ['rec', 'recommended']].forEach(([cls, spec]) => {
          const th = thead.querySelector(`.gbgb-specs-table__th--${cls}`);
          if (!th) return;
          const line = res ? verdictLine(res[spec]) : { tone: 'unknown', text: 'Checking your PC…' };
          if (!line) return;
          const verdict = document.createElement('span');
          verdict.className = `gbgb-specs-table__verdict is-${line.tone}`;
          verdict.textContent = line.text;
          th.appendChild(verdict);
        });
      }
      table.appendChild(thead);

      const tbody = document.createElement('div');
      tbody.className = 'gbgb-specs-table__body';

      allKeys.forEach((key) => {
        const minItem = minByKey.get(key);
        const recItem = recByKey.get(key);
        const label = (minItem && minItem.label) || (recItem && recItem.label) || NAMES[key] || key;

        const row = document.createElement('div');
        row.className = 'gbgb-specs-table__row';

        const catCell = document.createElement('div');
        catCell.className = 'gbgb-specs-table__cell gbgb-specs-table__cell--cat';
        catCell.innerHTML = `
          <div class="gbgb-specs__icon" aria-hidden="true">${ICONS[key] || ICONS.other}</div>
          <span class="gbgb-specs__label">${label}</span>
        `;
        row.appendChild(catCell);

        if (hasMin) {
          const minCell = document.createElement('div');
          minCell.className = 'gbgb-specs-table__cell gbgb-specs-table__cell--min';
          minCell.textContent = minItem ? minItem.value : '—';
          row.appendChild(minCell);
        }

        if (hasRec) {
          const recCell = document.createElement('div');
          recCell.className = 'gbgb-specs-table__cell gbgb-specs-table__cell--rec';
          recCell.textContent = recItem ? recItem.value : '—';
          row.appendChild(recCell);
        }

        tbody.appendChild(row);
      });

      table.appendChild(tbody);
      body.appendChild(table);
    }

    /* ---------- Continue ---------- */

    showError(msg, focusEl) {
      this.ui.error.textContent = msg;
      this.ui.error.hidden = false;
      if (focusEl) {
        focusEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        focusEl.focus({ preventScroll: true });
      }
    }

    async loadCpuTiers() {
      const url = this.dataset.cpuTiersUrl;
      if (!url) return;
      try {
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          this.cpuEngine = new HardwareTierEngine(data);
          if (this.catalogData) this.syncCatalogOfferings(this.catalogData);
          this.renderGoBar();
        }
      } catch (e) {
        // fallback engine is already initialized
      }
    }

    async loadGpuTiers() {
      const url = this.dataset.gpuTiersUrl;
      if (!url) return;
      try {
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          this.gpuEngine = new HardwareTierEngine(data);
          if (this.catalogData) this.syncCatalogOfferings(this.catalogData);
          this.renderGoBar();
        }
      } catch (e) {
        // fallback engine is already initialized
      }
    }

    async loadCases() {
      const url = this.dataset.casesUrl;
      if (!url) { this.caseBook = { cases: [] }; return; }
      try {
        const res = await fetch(url);
        this.caseBook = res.ok ? await res.json() : { cases: [] };
      } catch (e) {
        this.caseBook = { cases: [] };
      }
      this.renderGoBar();
    }

    async loadCatalog() {
      const url = this.dataset.catalogUrl || this.dataset.catalog;
      if (!url) return;
      try {
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          this.catalogData = data;
          this.syncCatalogOfferings(data);
        }
      } catch (e) {
        // fallback engine is already initialized
      }
    }

    syncCatalogOfferings(catalog) {
      if (!catalog || !catalog.parts) return;

      // Sync CPUs
      if (Array.isArray(catalog.parts.cpu) && this.cpuEngine && this.cpuEngine.brands) {
        const amdCpus = catalog.parts.cpu.filter(c => c.brand === 'amd');
        const intelCpus = catalog.parts.cpu.filter(c => c.brand === 'intel');

        if (amdCpus.length && this.cpuEngine.brands.amd) {
          const existingOffs = this.cpuEngine.brands.amd.offerings || [];
          const offMap = new Map(existingOffs.map(o => [o.id, o]));
          const synced = [];
          amdCpus.forEach((part, idx) => {
            const existing = offMap.get(part.id);
            if (existing) {
              synced.push(existing);
            } else {
              synced.push({
                id: part.id,
                name: part.label || part.id,
                shortName: part.label || part.id,
                tier: idx + 1,
                minRequiredTier: idx,
                cores: part.cores || 6,
                turbo: part.turbo || '4.0 GHz',
                badge: part.group || 'High Performance',
                summary: part.note || `Fast ${part.cores || 6}-core gaming processor.`
              });
            }
          });
          if (synced.length) {
            this.cpuEngine.brands.amd.offerings = synced.sort((a, b) => a.tier - b.tier);
          }
        }

        if (intelCpus.length && this.cpuEngine.brands.intel) {
          const existingOffs = this.cpuEngine.brands.intel.offerings || [];
          const offMap = new Map(existingOffs.map(o => [o.id, o]));
          const synced = [];
          intelCpus.forEach((part, idx) => {
            const existing = offMap.get(part.id);
            if (existing) {
              synced.push(existing);
            } else {
              synced.push({
                id: part.id,
                name: part.label || part.id,
                shortName: part.label || part.id,
                tier: (idx + 1) * 2,
                minRequiredTier: idx * 2,
                cores: part.cores || 8,
                turbo: part.turbo || '4.5 GHz',
                badge: part.group || 'Intel Core',
                summary: part.note || `High performance Intel processor.`
              });
            }
          });
          if (synced.length) {
            this.cpuEngine.brands.intel.offerings = synced.sort((a, b) => a.tier - b.tier);
          }
        }
      }

      // Sync GPUs
      if (Array.isArray(catalog.parts.gpu) && this.gpuEngine && this.gpuEngine.brands) {
        const nvGpus = catalog.parts.gpu.filter(g => g.brand === 'nvidia');
        const amdGpus = catalog.parts.gpu.filter(g => g.brand === 'radeon' || g.brand === 'amd');

        if (nvGpus.length && this.gpuEngine.brands.nvidia) {
          const existingOffs = this.gpuEngine.brands.nvidia.offerings || [];
          const offMap = new Map(existingOffs.map(o => [o.id, o]));
          const synced = [];
          nvGpus.forEach((part, idx) => {
            const existing = offMap.get(part.id);
            if (existing) {
              synced.push(existing);
            } else {
              synced.push({
                id: part.id,
                name: part.label || part.id,
                shortName: part.short || part.label || part.id,
                tier: 40 + idx * 3,
                vram: (part.facts && part.facts.find(f => /vram/i.test(f.label))?.value) || '8 GB GDDR6',
                badge: 'NVIDIA GeForce',
                summary: part.note || 'Next-gen gaming graphics.'
              });
            }
          });
          if (synced.length) {
            this.gpuEngine.brands.nvidia.offerings = synced.sort((a, b) => a.tier - b.tier);
          }
        }

        if (amdGpus.length && this.gpuEngine.brands.amd) {
          const existingOffs = this.gpuEngine.brands.amd.offerings || [];
          const offMap = new Map(existingOffs.map(o => [o.id, o]));
          const synced = [];
          amdGpus.forEach((part, idx) => {
            const existing = offMap.get(part.id);
            if (existing) {
              synced.push(existing);
            } else {
              synced.push({
                id: part.id,
                name: part.label || part.id,
                shortName: part.label || part.id,
                tier: 50 + idx * 2,
                vram: (part.facts && part.facts.find(f => /vram/i.test(f.label))?.value) || '16 GB GDDR6',
                badge: 'AMD Radeon',
                summary: part.note || 'High-performance RDNA graphics.'
              });
            }
          });
          if (synced.length) {
            this.gpuEngine.brands.amd.offerings = synced.sort((a, b) => a.tier - b.tier);
          }
        }
      }

      this.renderGoBar();
    }

    async go() {
      const btn = this.ui.go;
      if (btn.getAttribute('aria-busy') === 'true') return;
      this.ui.error.hidden = true;
      if (!this.selected.size) {
        this.showError('Pick at least one game to continue.', this.ui.step1);
        return;
      }
      if (this.productMode) {
        this.applyToConfigurator();
        return;
      }
      if (!this.caseSel) {
        this.showError('Pick a case to continue.', this.ui.step2);
        return;
      }

      btn.setAttribute('aria-busy', 'true');
      btn.textContent = 'Putting together your build';

      const games = [...this.selected.values()];
      // Use EXACTLY what the customer is looking at - never recompute or default here.
      const rec = this.shownRec;
      if (rec && rec.blocked) {
        btn.removeAttribute('aria-busy');
        this.applyGoState();
        return;
      }
      if (!rec) {
        btn.removeAttribute('aria-busy');
        btn.textContent = this._origCtaText || btn.textContent;
        this.showError('Still calculating your recommendation - give it a second and try again.', this.ui.step1);
        return;
      }
      const cpuEval = rec.cpuEval;
      const gpuEval = rec.gpuEval;
      const finalCpu = rec.cpu;
      const finalGpu = rec.gpu;

      const detail = {
        version: 1,
        createdAt: new Date().toISOString(),
        tier: this.tierSelection || 'recommended',
        cpuBrand: this.selectedCpuBrand,
        gpuBrand: this.selectedGpuBrand,
        ramGb: rec.ramGb,
        games: games.map((g) => ({
          appid: g.appid,
          slug: g.appid,
          rawgId: g.id || null,
          name: g.name,
          source: g.source,
          fps: g.fps || null,
          fpsFilter: g.fpsFilter || null,
          requirements: g.requirements
            ? {
                source: g.requirements.source,
                minimum: summarize(g.requirements.minimum),
                recommended: summarize(g.requirements.recommended),
                minimumRows: g.requirements.minimum,
                recommendedRows: g.requirements.recommended,
              }
            : null,
        })),
        case: {
          handle: this.caseSel.handle,
          title: this.caseSel.title,
          fullTitle: this.caseSel.fullTitle,
          url: this.caseSel.url,
          image: this.caseSel.image,
          price: this.caseSel.price,
        },
        hardwareRequirements: {
          cpu: cpuEval,
          gpu: gpuEval,
        },
        peakMinCpu: cpuEval ? cpuEval.peakMinimum : null,
        peakRecCpu: cpuEval ? cpuEval.peakRecommended : null,
        recommendedCpu: finalCpu,
        peakMinGpu: gpuEval ? gpuEval.peakMinimum : null,
        peakRecGpu: gpuEval ? gpuEval.peakRecommended : null,
        recommendedGpu: finalGpu,
      };

      try { sessionStorage.setItem(REQUEST_KEY, JSON.stringify(detail)); } catch (e) { /* ignore */ }
      this.currentRequestDetail = detail;
      this.track('gbg_build_request', { games: detail.games.map((x) => x.appid || x.slug), case: detail.case.handle });

      await this.proceedToCustomizer();
    }

    // Product page: the CPU and GPU brand tabs start from the PC the shopper has configured (an Intel PC is not switched to AMD by the
    // button) and follow it when it changes, until the shopper clicks a brand tab themselves. A GPU with no tab (Intel Arc) leaves its tab.
    followPc() {
      if (!this.productMode || this.brandChosen || !this.dataIsReady || !window.GBG_configurator) return;
      const pc = window.GBG_configurator.current();
      const part = (slot) => (this.catalogData.parts[slot] || []).find((p) => p.id === pc[slot]);
      const cpu = part('cpu');
      const gpu = part('gpu');
      if (cpu) this.setCpuBrand(cpu.brand, true);
      if (gpu && (gpu.brand === 'nvidia' || gpu.brand === 'radeon')) this.setGpuBrand(gpu.brand === 'radeon' ? 'amd' : 'nvidia', true);
    }

    // Product page: the parts shown in "We Recommend" go straight into the configurator on this page. No product is built and
    // nothing is opened. The configurator's engine answers for what cannot be done (it then changes nothing).
    applyToConfigurator() {
      const rec = this.shownRec;
      if (!rec) {
        this.showError('Still calculating your recommendation - give it a second and try again.');
        return;
      }
      if (rec.blocked) return;
      try {
        window.GBG_configurator.apply({ cpu: rec.cpu.id, gpu: rec.gpu.id, ramGb: rec.ramGb });
      } catch (e) {
        if (!e.code) throw e; // only the engine's refusals are for the shopper to read
        this.showError(e.message);
        return;
      }
      this.track('gbg_build_request', { games: [...this.selected.keys()], surface: 'product' });
      this.announce('Your PC now has the parts we recommend for your games.');
    }

    renderRecommendationModal(cpuEval, gpuEval, games) {
      const body = this.ui.recBody;
      if (!body) return;
      body.textContent = '';

      const peakMinCpu = cpuEval ? cpuEval.peakMinimum : null;
      const peakRecCpu = cpuEval ? cpuEval.peakRecommended : null;
      const recCpu = cpuEval ? cpuEval.recommendations : null;

      const peakMinGpu = gpuEval ? gpuEval.peakMinimum : null;
      const peakRecGpu = gpuEval ? gpuEval.peakRecommended : null;
      const recGpu = gpuEval ? gpuEval.recommendations : null;

      // 1. Games Strip
      const gamesWrap = document.createElement('div');
      gamesWrap.className = 'gbgb-rec__games';
      const gamesLabel = document.createElement('span');
      gamesLabel.className = 'gbgb-rec__games-label';
      gamesLabel.textContent = `Analyzed Lineup (${games.length} Game${games.length > 1 ? 's' : ''}):`;
      const gamesList = document.createElement('div');
      gamesList.className = 'gbgb-rec__games-list';

      games.forEach((g) => {
        const pill = document.createElement('span');
        const isDemanding = (peakRecCpu && g.name === peakRecCpu.gameName) ||
          (peakMinCpu && g.name === peakMinCpu.gameName) ||
          (peakRecGpu && g.name === peakRecGpu.gameName) ||
          (peakMinGpu && g.name === peakMinGpu.gameName);
        pill.className = `gbgb-rec__game-pill ${isDemanding ? 'is-demanding' : ''}`;
        pill.innerHTML = `
          ${isDemanding ? '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="#1eff2c" stroke-width="2.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>' : ''}
          <span>${clean(g.name)}</span>
        `;
        gamesList.appendChild(pill);
      });
      gamesWrap.append(gamesLabel, gamesList);

      // 2. Peak Requirements Grid (CPU & GPU)
      const peaksGrid = document.createElement('div');
      peaksGrid.className = 'gbgb-rec__peaks';

      const peakMinGame = (peakMinGpu && peakMinGpu.gameName) || (peakMinCpu && peakMinCpu.gameName) || 'All Games';
      const peakRecGame = (peakRecGpu && peakRecGpu.gameName) || (peakRecCpu && peakRecCpu.gameName) || 'All Games';

      // Peak Minimum Card
      const minCard = document.createElement('div');
      minCard.className = 'gbgb-rec__peak-card gbgb-rec__peak-card--min';
      minCard.innerHTML = `
        <div class="gbgb-rec__peak-header">
          <span class="gbgb-rec__peak-tag">Peak Minimum Spec</span>
          <span class="gbgb-rec__peak-game" title="${peakMinGame}">${peakMinGame}</span>
        </div>
        <div class="gbgb-rec__peak-models">
          ${peakMinCpu ? `<div class="gbgb-rec__peak-model"><span style="color:var(--gbgb-green);font-size:12px;font-weight:800;display:block;margin-bottom:2px;">CPU:</span>${peakMinCpu.intel ? peakMinCpu.intel.model : ''} / ${peakMinCpu.amd ? peakMinCpu.amd.model : ''}</div>` : ''}
          ${peakMinGpu ? `<div class="gbgb-rec__peak-model" style="margin-top:8px;"><span style="color:var(--gbgb-green);font-size:12px;font-weight:800;display:block;margin-bottom:2px;">GPU:</span>${peakMinGpu.nvidia ? peakMinGpu.nvidia.model : ''} / ${peakMinGpu.secondary ? peakMinGpu.secondary.model : ''}</div>` : ''}
        </div>
        <p class="gbgb-rec__peak-raw">Most demanding minimum requirements among your selected games.</p>
      `;

      // Peak Recommended Card
      const recCard = document.createElement('div');
      recCard.className = 'gbgb-rec__peak-card gbgb-rec__peak-card--rec';
      recCard.innerHTML = `
        <div class="gbgb-rec__peak-header">
          <span class="gbgb-rec__peak-tag gbgb-rec__peak-tag--glow">Peak Recommended Spec</span>
          <span class="gbgb-rec__peak-game" title="${peakRecGame}">${peakRecGame}</span>
        </div>
        <div class="gbgb-rec__peak-models">
          ${peakRecCpu ? `<div class="gbgb-rec__peak-model"><span style="color:var(--gbgb-green);font-size:12px;font-weight:800;display:block;margin-bottom:2px;">CPU:</span>${peakRecCpu.intel ? peakRecCpu.intel.model : ''} / ${peakRecCpu.amd ? peakRecCpu.amd.model : ''}</div>` : ''}
          ${peakRecGpu ? `<div class="gbgb-rec__peak-model" style="margin-top:8px;"><span style="color:var(--gbgb-green);font-size:12px;font-weight:800;display:block;margin-bottom:2px;">GPU:</span>${peakRecGpu.nvidia ? peakRecGpu.nvidia.model : ''} / ${peakRecGpu.secondary ? peakRecGpu.secondary.model : ''}</div>` : ''}
        </div>
        <p class="gbgb-rec__peak-raw">Target hardware required for recommended 60+ FPS high settings.</p>
      `;
      peaksGrid.append(minCard, recCard);

      // 3. GBG Offerings Section — CPU
      const cpuMatchesWrap = document.createElement('div');
      cpuMatchesWrap.className = 'gbgb-rec__matches';
      const peakCpuGameDisplay = peakRecCpu && peakRecCpu.gameName ? peakRecCpu.gameName : (games[0] ? games[0].name : 'your games');
      cpuMatchesWrap.innerHTML = `
        <div class="gbgb-rec__matches-head">
          <h4 class="gbgb-rec__matches-title">Recommended Processor (CPU)</h4>
          <p class="gbgb-rec__matches-sub">Based on highest CPU demand (<strong>${peakCpuGameDisplay}</strong>):</p>
        </div>
      `;

      const cpuOffsGrid = document.createElement('div');
      cpuOffsGrid.className = 'gbgb-rec__offering-grid';

      if (recCpu && recCpu.amd) {
        const isPrim = !recCpu.primary || recCpu.primary.id === recCpu.amd.id;
        const amdCard = document.createElement('div');
        amdCard.className = `gbgb-rec__offering-card ${isPrim ? 'is-primary' : ''}`;
        amdCard.innerHTML = `
          <div class="gbgb-rec__offering-top">
            <span class="gbgb-brand-badge gbgb-brand-badge--amd">AMD Ryzen</span>
            ${isPrim ? '<span class="gbgb-rec__primary-badge">Top Gaming Pick</span>' : ''}
          </div>
          <h5 class="gbgb-rec__offering-name">${recCpu.amd.name}</h5>
          <div class="gbgb-rec__offering-specs">
            <span>${recCpu.amd.cores} Cores</span> • <span>${recCpu.amd.turbo} Turbo</span>
          </div>
          <div class="gbgb-rec__offering-highlight">${recCpu.amd.badge}</div>
          <p class="gbgb-rec__offering-desc">${recCpu.amd.summary}</p>
        `;
        cpuOffsGrid.appendChild(amdCard);
      }

      if (recCpu && recCpu.intel) {
        const isPrim = recCpu.primary && recCpu.primary.id === recCpu.intel.id;
        const intelCard = document.createElement('div');
        intelCard.className = `gbgb-rec__offering-card ${isPrim ? 'is-primary' : ''}`;
        intelCard.innerHTML = `
          <div class="gbgb-rec__offering-top">
            <span class="gbgb-brand-badge gbgb-brand-badge--intel">Intel Core</span>
            ${isPrim ? '<span class="gbgb-rec__primary-badge">Top Creator Pick</span>' : ''}
          </div>
          <h5 class="gbgb-rec__offering-name">${recCpu.intel.name}</h5>
          <div class="gbgb-rec__offering-specs">
            <span>${recCpu.intel.cores} Cores</span> • <span>${recCpu.intel.turbo} Turbo</span>
          </div>
          <div class="gbgb-rec__offering-highlight">${recCpu.intel.badge}</div>
          <p class="gbgb-rec__offering-desc">${recCpu.intel.summary}</p>
        `;
        cpuOffsGrid.appendChild(intelCard);
      }
      cpuMatchesWrap.appendChild(cpuOffsGrid);

      // 4. GBG Offerings Section — GPU
      const gpuMatchesWrap = document.createElement('div');
      gpuMatchesWrap.className = 'gbgb-rec__matches';
      const peakGpuGameDisplay = peakRecGpu && peakRecGpu.gameName ? peakRecGpu.gameName : (games[0] ? games[0].name : 'your games');
      gpuMatchesWrap.innerHTML = `
        <div class="gbgb-rec__matches-head">
          <h4 class="gbgb-rec__matches-title">Recommended Graphics Card (GPU)</h4>
          <p class="gbgb-rec__matches-sub">Based on highest GPU demand (<strong>${peakGpuGameDisplay}</strong>):</p>
        </div>
      `;

      const gpuOffsGrid = document.createElement('div');
      gpuOffsGrid.className = 'gbgb-rec__offering-grid';

      if (recGpu && recGpu.nvidia) {
        const isPrim = !recGpu.primary || recGpu.primary.id === recGpu.nvidia.id;
        const nvCard = document.createElement('div');
        nvCard.className = `gbgb-rec__offering-card ${isPrim ? 'is-primary' : ''}`;
        nvCard.innerHTML = `
          <div class="gbgb-rec__offering-top">
            <span class="gbgb-brand-badge gbgb-brand-badge--nvidia">NVIDIA GeForce</span>
            ${isPrim ? '<span class="gbgb-rec__primary-badge">Top Graphics Pick</span>' : ''}
          </div>
          <h5 class="gbgb-rec__offering-name">${recGpu.nvidia.name}</h5>
          <div class="gbgb-rec__offering-specs">
            <span>${recGpu.nvidia.vram}</span>
          </div>
          <div class="gbgb-rec__offering-highlight">${recGpu.nvidia.badge}</div>
          <p class="gbgb-rec__offering-desc">${recGpu.nvidia.summary}</p>
        `;
        gpuOffsGrid.appendChild(nvCard);
      }
      gpuMatchesWrap.appendChild(gpuOffsGrid);

      // 5. Guarantee Banner
      const guarantee = document.createElement('div');
      guarantee.className = 'gbgb-rec__guarantee';
      guarantee.innerHTML = `
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M20 6L9 17l-5-5" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="square"/></svg>
        <span>Meets or exceeds 100% of recommended specs for all selected games.</span>
      `;

      body.append(gamesWrap, peaksGrid, cpuMatchesWrap, gpuMatchesWrap, guarantee);
    }

    assembleCustomBuildPayload(detail) {
      const caseSel = (detail && detail.case) || this.caseSel || {};
      const recCpu = (detail && detail.recommendedCpu) || null;
      const recGpu = (detail && detail.recommendedGpu) || null;

      const cpuLabel = (recCpu && (recCpu.shortName || recCpu.name)) || (this.selectedCpuBrand === 'intel' ? 'i5 12400F' : 'Ryzen 5 5500');
      const gpuLabel = (recGpu && (recGpu.shortName || recGpu.name)) || (this.selectedGpuBrand === 'amd' ? 'RX 9070 16GB' : 'GTX 1660 SUPER');
      const ramGb = RAM_CHOICES.includes(Number(detail && detail.ramGb)) ? Number(detail.ramGb) : 0;

      const cleanCaseTitle = String(caseSel.title || 'Custom PC').split(' - ')[0].split(' — ')[0].trim();
      // The configurator reads everything off the product title, so the generated title is
      // the picked case product's own full title with only the CPU and GPU swapped. The
      // worker then corrects PSU wattage / DDR generation for those parts and prices it
      // from the catalog. Segments are found by content, not position.
      const title = (() => {
        const segs = String(caseSel.fullTitle || '').split(',').map((s) => s.trim()).filter(Boolean);
        // the RAM kit goes BEFORE the GPU: the engine reads the first "<N>GB" that is a kit we sell (an RTX "16GB" would be one)
        const plain = `${cleanCaseTitle}, ${cpuLabel}, ${ramGb ? ramGb + 'GB DDR4, ' : ''}${gpuLabel}`;
        if (segs.length < 3) return plain;
        segs[0] = cleanCaseTitle;
        const cpuIdx = segs.findIndex((s, i) => i > 0 && /\b(ryzen|i[3579]|ultra|intel|core)\b/i.test(s));
        const gpuIdx = segs.findIndex((s, i) => i > 0 && /\b(rtx|gtx|rx|radeon|arc)\b/i.test(s));
        const ramIdx = segs.findIndex((s, i) => i > 0 && /^\d+\s*gb\b/i.test(s) && /\bddr[45]\b/i.test(s));
        if (cpuIdx > 0) segs[cpuIdx] = cpuLabel;
        if (gpuIdx > 0) segs[gpuIdx] = gpuLabel;
        if (ramGb && ramIdx > 0) segs[ramIdx] = segs[ramIdx].replace(/^\d+\s*gb/i, ramGb + 'GB'); // keeps the DDR type and any RGB wording
        if (cpuIdx < 0 || gpuIdx < 0) return plain;
        return segs.join(', ');
      })();

      let imageUrl = caseSel.image || '';
      if (imageUrl.startsWith('//')) imageUrl = 'https:' + imageUrl;

      // Generate a description blurb for auto-game-configured builds.
      const gameNames = (detail && detail.games || []).map((g) => g.name).filter(Boolean);
      const tierLabel = (() => {
        const t = (detail && detail.tier) || this.tierSelection || 'recommended';
        if (t === 'minimum') return 'Minimum';
        if (t === 'boost') return 'Performance Boost';
        return 'Recommended';
      })();
      const blurb = gameNames.length
        ? generateModularBlurb(cpuLabel, gpuLabel, gameNames, tierLabel)
        : '';

      // No case_title/cpu/gpu fields: when all three are present the worker builds its own
      // short "case, cpu, gpu" title and ignores `title`. normalize: the worker builds this title
      // with the configurator's own engine (PSU/RAM/cooler fixes, title) and prices it itself -
      // no price is sent from here (one price list: gbg-part-pricing.json).
      return {
        title,
        normalize: true,
        image_url: imageUrl,
        image_media_id: caseSel.mediaId || '',
        blurb,
      };

    }

    showLoadingOverlay() {
      if (window.GBGLoading) window.GBGLoading.show(this.ui.loadingOverlay);
    }

    hideLoadingOverlay() {
      if (window.GBGLoading) window.GBGLoading.hide(this.ui.loadingOverlay);
    }

    resetLoadingState() {
      const ctaBtn = this.ui && this.ui.go;
      const recBtn = this.ui && this.ui.recProceed;
      this.hideLoadingOverlay();
      if (ctaBtn) {
        ctaBtn.removeAttribute('aria-busy');
        ctaBtn.disabled = false;
        if (this._origCtaText) ctaBtn.textContent = this._origCtaText;
      }
      if (recBtn) {
        recBtn.removeAttribute('aria-busy');
        recBtn.disabled = false;
        if (this._origRecHtml) recBtn.innerHTML = this._origRecHtml;
      }
      this.applyGoState();
    }

    async proceedToCustomizer() {
      const detail = this.currentRequestDetail;
      if (!detail) return;
      if (!this.shownRec || this.shownRec.blocked) return;
      const proceed = this.dispatchEvent(new CustomEvent('gbg:build-request', { detail, bubbles: true, cancelable: true }));
      if (!proceed) return;

      const ctaBtn = this.ui.go;
      const recBtn = this.ui.recProceed;

      const setLoading = (busy) => {
        if (busy) this.showLoadingOverlay();
        else this.hideLoadingOverlay();
        if (ctaBtn) {
          ctaBtn.toggleAttribute('aria-busy', busy);
          ctaBtn.disabled = busy;
        }
        if (recBtn) {
          recBtn.toggleAttribute('aria-busy', busy);
          recBtn.disabled = busy;
        }
      };

      setLoading(true);

      const payload = this.assembleCustomBuildPayload(detail);
      const apiOrigin = (this.dataset && this.dataset.apiUrl) || 'https://gbg-api.gbgcloudhelper.workers.dev';

      try {
        const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 9000));
        const res = await Promise.race([
          fetch(`${apiOrigin}/builds`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          }).then((r) => r.json().then((body) => ({ ok: r.ok, body }))),
          timeoutPromise,
        ]);

        if (res && res.ok && res.body && res.body.ok && res.body.handle) {
          const destUrl = new URL(`/products/${res.body.handle}`, window.location.origin);
          if (detail.games && detail.games.length) {
            destUrl.searchParams.set('gbg_games', detail.games.map((x) => x.appid || x.slug).join(','));
          }
          if (detail.case && detail.case.handle) {
            destUrl.searchParams.set('gbg_case', detail.case.handle);
          }
          console.log('[GBG Build Finder] Redirecting to customized product:', destUrl.toString());
          window.location.assign(destUrl.toString());
          return;
        }
        throw new Error((res && res.body && res.body.error) || 'Failed to create build');
      } catch (err) {
        console.warn('[GBG Build Finder] Dynamic spin-up fallback:', err);
        this.resetLoadingState();
        const base = this.dataset.customizerUrl || (this.caseSel && this.caseSel.url) || '/collections/all';
        const url = new URL(base, window.location.origin);
        if (detail.games && detail.games.length) {
          url.searchParams.set('gbg_games', detail.games.map((x) => x.appid || x.slug).join(','));
        }
        if (detail.case && detail.case.handle) {
          url.searchParams.set('gbg_case', detail.case.handle);
        }
        console.log('[GBG Build Finder] Redirecting to fallback customizer:', url.toString());
        window.location.assign(url.toString());
      }
    }

    // One row per game looked up with the searchbar (worker src/game-searches.js -> the dashboard's GAME SEARCHES tab):
    // the game, the specs Steam lists, what we recommend, and which page it was used on. Nothing about the shopper.
    logSearch(g) {
      try {
        const api = String(this.dataset.apiUrl || '').replace(/\/+$/, '');
        if (!api || !g || !/^\d+$/.test(String(g.appid))) return;
        this._logged = this._logged || new Map();
        const last = this._logged.get(g.appid);
        if (last && Date.now() - last < 60000) return;
        this._logged.set(g.appid, Date.now());
        const surface = this.dataset.surface || (this.productMode ? 'product' : 'by-game');
        const min = recommendFor(g, 'minimum', this.cpuEngine, this.gpuEngine);
        const rec = recommendFor(g, 'recommended', this.cpuEngine, this.gpuEngine);
        const body = JSON.stringify({
          surface, appid: String(g.appid), name: g.name,
          min: specLine(g, 'minimum'), rec: specLine(g, 'recommended'), gbg_min: min.text, gbg_rec: rec.text,
        });
        // text/plain: a "simple" cross-site request, so the browser sends it without a preflight round trip
        fetch(`${api}/api/game-search`, { method: 'POST', headers: { 'Content-Type': 'text/plain;charset=UTF-8' }, body, keepalive: true }).catch(() => {});
      } catch (e) { /* logging never blocks the page */ }
    }

    track(name, data) {
      try {
        const a = window.Shopify && window.Shopify.analytics;
        if (a && typeof a.publish === 'function') a.publish(name, data || {});
      } catch (e) { /* never block the UI on analytics */ }
    }
  }

  customElements.define('gbg-build-finder', GbgBuildFinder);

  // Exposed for testing and for the future customizer integration.
  window.HardwareTierEngine = HardwareTierEngine;
  window.GbgBuildFinder = { parseSpecs, parseManual, summarize, requestKey: REQUEST_KEY, HardwareTierEngine, GetRecommendedGBGBuild, CUSTOMIZER_NVIDIA_OFFERINGS,
    checkRequirements, verdictLine, recommendFor, recommendedRamGb, ramKitFor, specLine, specGb, pcGpuInfo, pcCpuInfo, DEFAULT_CPU_TIERS, DEFAULT_GPU_TIERS };
})();
